import * as THREE from 'three'
import type { MaterialName } from '../render/MaterialNames'
import type { Rand } from '../core/Rand'
import type { Surface } from '../core/Types'
import { POSES } from '../core/Poses'
import {
  ARRIS, Builder, CHAMFER, DOOR_H, DOOR_W, PARAPET_H, SILL_H, SLAB_T, STOREY, UP, WALL_T, WINDOW_H,
  catenary, chamferBox, clothQuad, cylinderGeom, decalQuad, hash2, impactChip, plainBox, planeQuad,
  rampPrism, shiftUv, sphereGeom, rotRectSdf,
} from './Kit'
import { TERRAIN_EDGE, groundHeight, settleHeight } from './Terrain'

/**
 * The modular architecture kit and the district's building list.
 *
 * Walls are authored as panels with real openings cut through a 0.34 m
 * thickness, so every window and doorway has genuine reveal depth rather than
 * a painted-on rectangle. Everything is chamfered, trimmed and stained.
 */

export type Side = 'n' | 'e' | 's' | 'w'

export type GroundStyle = 'blank' | 'windows' | 'shop' | 'door' | 'shopdoor' | 'arch' | 'garage' | 'ruin'
export type UpperStyle = 'blank' | 'windows' | 'balcony' | 'loggia'

export interface FacadeSpec {
  ground: GroundStyle
  upper: UpperStyle
  /** Bay spacing in metres; window rhythm derives from it. */
  bay?: number
}

/** Interior dividing wall, in the building's local frame. */
export interface PartitionSpec {
  axis: 'x' | 'z'
  /** Offset along the axis perpendicular to the wall. */
  at: number
  from: number
  to: number
  storey: number
  openings?: Opening[]
  mat?: MaterialName
}

export interface BuildingSpec {
  id: string
  cx: number
  cz: number
  w: number
  d: number
  yaw?: number
  storeys: number
  storeyH?: number
  wall: MaterialName
  trim?: MaterialName
  roof?: MaterialName
  parapet?: number
  faces: Partial<Record<Side, FacadeSpec>>
  /** Player-accessible interior: floors, partitions, stairs to the roof. */
  enterable?: boolean
  partitions?: PartitionSpec[]
  /** 0..1 — pockmarks, cracked render, missing parapet sections. */
  damage?: number
  roofClutter?: number
  /** Ground-floor slab sits this far above the plinth top. */
  interiorFloor?: boolean
}

// ---------------------------------------------------------------------------
// Wall panels
// ---------------------------------------------------------------------------

export interface Opening {
  /** Centre distance along the wall from its start. */
  at: number
  width: number
  /** Bottom of the opening above the wall base. */
  sill: number
  height: number
  kind: 'door' | 'window' | 'arch' | 'hole' | 'shop'
  glass?: boolean
  shutters?: boolean
  bars?: boolean
  broken?: boolean
}

export interface WallSpec {
  length: number
  height: number
  thickness: number
  mat: MaterialName
  trim: MaterialName
  openings: Opening[]
  exterior: boolean
  /** Projecting base course height, 0 for none. */
  plinth?: number
  cornice?: boolean
  /** 0..1 — staining, soot and bullet damage. */
  weather?: number
  surface?: Surface
  rng: Rand
  /** Skips collider generation (used for decorative infill). */
  noCollide?: boolean
  /**
   * Puts a dark, five-sided recess box behind every small opening. Set on
   * buildings with no modelled interior: without it a window is a hole onto
   * whatever light-blocking shell sits behind it, which renders as a pale grey
   * smear and destroys the read of the opening as depth.
   */
  backOpenings?: boolean
  /**
   * Suppresses the sill band and pilasters. Set on boundary walls, which
   * already carry their own pier rhythm and coping.
   */
  plain?: boolean
  /**
   * Dresses the room side of the panel as well as the street side. Exterior
   * walls only ever carried relief on the face they were authored from, so the
   * inside of every enterable building was a bare plane — which is most of what
   * the interior camera sees.
   */
  dressInside?: boolean
  /**
   * True on storeys above the ground. Projecting window boxes belong upstairs
   * and canopies belong downstairs; the wall has no other way to tell.
   */
  upperFloor?: boolean
}

/**
 * Render that has blown off the wall, modelled rather than decalled.
 *
 * A pale quad stuck on the surface reads as paper taped to the building — it
 * has no thickness, no shadow and no reason to be there. This exposes a panel
 * of the substrate and edges it with a broken lip of the render standing 3 cm
 * proud, which self-shadows, catches the sun on its arris and reads as damage
 * with a history.
 */
function spallPatch(
  b: Builder, render: MaterialName, sub: MaterialName,
  w: number, h: number, x: number, y: number, out: number, rng: Rand,
): void {
  b.geom(sub, decalQuad(w, h, 0.16, x * 7 + y), xform(x, y, out - 0.008))
  const lip = 0.035
  for (const side of [0, 1, 2, 3]) {
    const along = side < 2 ? w : h
    let t = -along / 2 + rng.range(0, along * 0.3)
    while (t < along / 2 - 0.06) {
      const seg = Math.min(along / 2 - t, rng.range(0.16, 0.42))
      const c = t + seg / 2
      const p = lip * rng.range(0.55, 1.2)
      // Plain boxes: at 3 cm proud the lip's own arris is sub-pixel, so the
      // relief and the shadow it throws are the whole effect and a chamfer
      // would cost four times the triangles for nothing.
      if (side === 0) b.geom(render, plainBox(seg, 0.06, p), xform(x + c, y + h / 2, out - p / 2))
      else if (side === 1) b.geom(render, plainBox(seg, 0.06, p), xform(x + c, y - h / 2, out - p / 2))
      else if (side === 2) b.geom(render, plainBox(0.06, seg, p), xform(x - w / 2, y + c, out - p / 2))
      else b.geom(render, plainBox(0.06, seg, p), xform(x + w / 2, y + c, out - p / 2))
      t += seg + rng.range(0.06, 0.3)
    }
  }
}

/** A louvred wall vent: frame, sill and four angled blades. */
function ventGrille(b: Builder, x: number, y: number, out: number, w: number, h: number): void {
  b.geom('concreteWorn', chamferBox(w + 0.11, h + 0.11, 0.07, 0.016), xform(x, y, out - 0.02))
  b.geom('metalRusted', plainBox(w, h, 0.03), xform(x, y, out - 0.055))
  // Blades pitch about X so they shed downward and outward, the way a louvre
  // does; rolling them about Z would just cant them across the opening.
  const blades = Math.max(3, Math.round(h / 0.075))
  for (let i = 0; i < blades; i++) {
    b.geom('metalRusted', plainBox(w - 0.02, 0.038, 0.05),
      xform(x, y - h / 2 + ((i + 0.5) * h) / blades, out - 0.072, 0, 0.5))
  }
}

/** Gooseneck wall lamp on a bracket — a strong small silhouette on a facade. */
function wallLamp(b: Builder, x: number, y: number, out: number): void {
  b.geom('metalRusted', plainBox(0.09, 0.14, 0.07), xform(x, y, out - 0.03))
  // Arm running from the bracket up and away from the wall (-Z is outward).
  b.geom('metalRusted', cylinderGeom(0.017, 0.017, 0.36, 5),
    xform(x, y + 0.11, out - 0.205, 0, -Math.PI / 3))
  b.geom('metalPainted', cylinderGeom(0.13, 0.04, 0.12, 10), xform(x, y + 0.17, out - 0.36, 0, Math.PI))
  b.geom('glass', sphereGeom(0.05, 8, 6), xform(x, y + 0.11, out - 0.36))
}

function xform(x: number, y: number, z: number, yaw = 0, pitch = 0, roll = 0): THREE.Matrix4 {
  const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, yaw, roll, 'YXZ'))
  return new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), q, new THREE.Vector3(1, 1, 1))
}

const _ONE = new THREE.Vector3(1, 1, 1)

/**
 * A round member spanning two points in the current local frame.
 *
 * Diagonals — awning stays, aerial guys, bracing — are the only lines on a
 * facade that are neither horizontal nor vertical, which is exactly why they
 * break up a wall so effectively. Authoring them by hand from an Euler triple
 * is where the sign errors live, so this takes the two ends instead.
 */
function strut(
  b: Builder, mat: MaterialName,
  ax: number, ay: number, az: number,
  cx: number, cy: number, cz: number,
  r: number, seg = 4,
): void {
  const dir = new THREE.Vector3(cx - ax, cy - ay, cz - az)
  const len = dir.length()
  if (len < 1e-4) return
  const q = new THREE.Quaternion().setFromUnitVectors(UP, dir.divideScalar(len))
  const mid = new THREE.Vector3((ax + cx) / 2, (ay + cy) / 2, (az + cz) / 2)
  b.geom(mat, cylinderGeom(r, r, len, seg), new THREE.Matrix4().compose(mid, q, _ONE))
}

/**
 * A split-system condenser bracketed to the wall, two metres up.
 *
 * Nothing already on these facades projects more than 15 cm, and at the 15-30 m
 * the graded cameras work at, 15 cm of relief is a one-pixel shadow. This
 * stands 40 cm off the render, is nearly a metre wide, and throws a hard
 * rectangle of shade across the wall under it — which is the scale of incident
 * that actually reads as depth at that distance.
 */
function wallCondenser(b: Builder, x: number, y: number, out: number, rng: Rand): void {
  const w = rng.range(0.7, 0.92)
  const h = rng.range(0.48, 0.62)
  const dp = 0.3
  const z = out - 0.08 - dp / 2
  // Angle brackets under it, with a diagonal stay back to the wall.
  for (const sx of [-1, 1]) {
    const bx = x + sx * (w / 2 - 0.08)
    b.slab('metalRusted', 0.05, 0.045, dp + 0.14, bx, y - h / 2 - 0.03, out - (dp + 0.14) / 2)
    strut(b, 'metalRusted', bx, y - h / 2 - 0.05, out - dp - 0.06, bx, y - h / 2 - 0.42, out - 0.02, 0.014)
  }
  b.box('metalPainted', w, h, dp, x, y, z, 0, 0.022)
  b.slab('metalPainted', w + 0.03, 0.022, dp + 0.03, x, y + h / 2 - 0.02, z)
  // Discharge guard: a proud ring with radial bars over a recessed fan.
  const r = Math.min(0.19, h * 0.38)
  b.geom('metalRusted', cylinderGeom(r, r, 0.04, 12), xform(x, y, z - dp / 2 + 0.02, 0, Math.PI / 2))
  b.geom('metalRusted', cylinderGeom(r + 0.03, r + 0.03, 0.028, 14, false), xform(x, y, z - dp / 2 - 0.02, 0, Math.PI / 2))
  for (let k = 0; k < 4; k++) {
    b.geom('metalRusted', plainBox(r * 2, 0.013, 0.013), xform(x, y, z - dp / 2 - 0.03, 0, 0, (k * Math.PI) / 4))
  }
  // Lagged refrigerant pair elbowing back into the wall, and the condensate
  // trail it has been washing down the render for years.
  for (const off of [-0.05, 0.05]) {
    b.geom('metalPainted', cylinderGeom(0.025, 0.025, 0.34, 5), xform(x + w / 2 + 0.02, y - 0.1 + off, z, 0, 0, Math.PI / 2))
  }
  b.geom('dirt', decalQuad(w * 0.55, rng.range(0.9, 1.9), 0.3, x * 11 + y), xform(x + rng.spread(0.1), y - h / 2 - 0.8, out - 0.013))
}

/**
 * A latticed timber window box projecting from an upper storey.
 *
 * The regional signature, and by a distance the strongest thing that can be put
 * on a blank upper facade: half a metre of projection gives it a lit top, two
 * shaded returns and a cast shadow, and the lattice carries relief finer than
 * anything else on the building, so it holds up both at 30 m and at 3 m.
 */
function mashrabiya(
  b: Builder, trim: MaterialName, x: number, sill: number, w: number, h: number, out: number, rng: Rand,
): void {
  const proj = rng.range(0.46, 0.64)
  const front = out - proj
  const cy = sill + h / 2
  // The box is centred over a window wherever there is one, so without a back
  // the lattice is a screen in front of a sheet of glazing — and glazing on a
  // shaded facade mirrors open sky. In the `sunset` pose that turned this into
  // a grid of blown-out white squares reading as a punch card. A real screen
  // shows a dark room behind it from the street, so the box gets a back panel
  // just clear of the render.
  b.plate('asphalt', w - 0.06, h - 0.06, 0.05, x, cy, out - 0.05)
  // Stone corbels and the timber sole plate they carry.
  for (const sx of [-1, 1]) {
    b.slab(trim, 0.14, 0.22, proj + 0.02, x + sx * (w / 2 - 0.12), sill - 0.16, out - (proj + 0.02) / 2)
  }
  b.slab('woodPainted', w + 0.14, 0.085, proj + 0.1, x, sill - 0.02, out - (proj + 0.1) / 2)
  // Corner posts front and back.
  for (const sx of [-1, 1]) {
    b.slab('woodPainted', 0.07, h, 0.07, x + sx * (w / 2 - 0.035), cy, out - 0.045)
    b.slab('woodPainted', 0.07, h, 0.07, x + sx * (w / 2 - 0.035), cy, front + 0.04)
  }
  // Lattice across the front and both returns.
  const cols = Math.max(5, Math.round(w / 0.17))
  const rows = Math.max(4, Math.round(h / 0.2))
  for (let i = 1; i < cols; i++) {
    b.plate('woodPainted', 0.026, h - 0.1, 0.028, x - w / 2 + (w * i) / cols, cy, front + 0.02)
  }
  for (let j = 1; j < rows; j++) {
    b.plate('woodPainted', w - 0.07, 0.024, 0.028, x, sill + (h * j) / rows, front + 0.02)
  }
  const rets = Math.max(2, Math.round(proj / 0.16))
  for (const sx of [-1, 1]) {
    const px = x + sx * (w / 2 - 0.03)
    for (let i = 1; i < rets; i++) {
      b.plate('woodPainted', 0.026, h - 0.1, 0.026, px, cy, front + (proj * i) / rets)
    }
    for (let j = 1; j < rows; j++) {
      b.plate('woodPainted', 0.026, 0.024, proj - 0.08, px, sill + (h * j) / rows, out - proj / 2)
    }
  }
  // Pitched lid with an overhang, and a rail under it.
  b.slab('metalCorrugated', w + 0.2, 0.035, proj + 0.16, x, sill + h + 0.04, out - (proj + 0.16) / 2, 0)
  b.slab('woodPainted', w + 0.1, 0.075, 0.08, x, sill + h - 0.02, front + 0.04)
  // Rust wash off the lid edge.
  b.geom('dirt', decalQuad(w * 0.9, 0.5, 0.26, x * 5 + sill), xform(x, sill - 0.45, out - 0.012))
}

/**
 * A pitched sheet canopy over a ground-floor opening, on angle brackets.
 *
 * A canopy is worth more than its own silhouette: it lays a hard diagonal band
 * of shade down the wall and across the pavement in front of the shop, which is
 * luminance variation no amount of surface detail produces.
 */
function shopCanopy(b: Builder, x: number, top: number, w: number, out: number, rng: Rand): void {
  // Capped at 1.25 m: the alley is under 3 m wide and canopies on both sides
  // would meet over the middle of it.
  const proj = rng.range(0.9, 1.25)
  const drop = rng.range(0.16, 0.34)
  const y = top + rng.range(0.18, 0.36)
  const cw = w + rng.range(0.3, 0.7)
  const run = Math.hypot(proj, drop)
  for (const sx of [-1, 1]) {
    const bx = x + sx * (cw / 2 - 0.09)
    b.slab('metalRusted', 0.045, 0.045, proj, bx, y - drop / 2, out - proj / 2)
    strut(b, 'metalRusted', bx, y - drop, out - proj + 0.03, bx, y + 0.52, out - 0.02, 0.015)
  }
  // The sheet itself, pitched so the outward edge is the low one.
  b.geom('metalCorrugated', chamferBox(cw, 0.035, run, 0.009),
    xform(x, y - drop / 2, out - proj / 2, 0, -Math.atan2(drop, proj)))
  // Fascia board along the drip edge, and a purlin behind it.
  b.slab('woodPainted', cw + 0.04, 0.11, 0.035, x, y - drop - 0.05, out - proj)
  b.slab('metalRusted', cw, 0.045, 0.045, x, y - drop * 0.45 + 0.05, out - proj * 0.5)
  b.geom('dirt', decalQuad(cw * 0.8, 0.4, 0.24, x * 3 + top), xform(x, y + 0.28, out - 0.013))
}

/**
 * The spill of broken masonry that collects where a wall meets the ground.
 *
 * Sand is already banked against the building footprints, but sand is smooth
 * and the junction still resolved as one clean ruled line where the wall met
 * the paving. What breaks that line is hard rubbish with its own edges: fallen
 * blocks, spalled render, mortar crumb and a weed in the gap. Every piece sits
 * half buried, so the line the eye follows along the base is broken rather than
 * softened.
 */
function baseSpill(b: Builder, s: WallSpec, L: number, T: number, rng: Rand): void {
  const out = -T / 2
  const probe = new THREE.Vector3()
  /**
   * Local y of the drawn ground under a point on this wall.
   *
   * A building's base sits on the *highest* terrain sample under its footprint,
   * so local y = 0 stands as much as 30 cm above the ground at the low corner.
   * Spilling rubble at a fixed local height would float it exactly where the
   * near-field cameras look. Only yaw is ever pushed, so world y and local y
   * stay parallel and one probe per piece is enough.
   */
  const drop = (x: number, z: number): number => {
    b.localToWorld(x, 0, z, probe)
    return settleHeight(probe.x, probe.z, 0.3, 0.01) - probe.y
  }
  let x = rng.range(0.3, 1.6)
  while (x < L - 0.25) {
    const kind = rng.next()
    const z = out - rng.range(0.06, 0.34)
    const y0 = drop(x, z)
    if (kind < 0.42) {
      // A fallen block, part sunk and rotated off the wall line. Built from the
      // wall's own stone, so it reads as having come off this building — and so
      // it lands in a batch the block already has and still casts a contact
      // shadow, which `concreteRubble` (excluded from the shadow pass because
      // it also carries flat spall sheets) would not.
      const bh = rng.range(0.12, 0.2)
      b.geom(rng.bool(0.5) ? s.trim : s.mat, chamferBox(rng.range(0.22, 0.42), bh, rng.range(0.15, 0.26), 0.018),
        xform(x, y0 + bh * rng.range(0.24, 0.42), z, rng.range(0, Math.PI), rng.spread(0.22), rng.spread(0.28)))
    } else if (kind < 0.68) {
      // Crumb: three or four chips too small to be worth a chamfer.
      for (let i = 0; i < rng.int(3, 5); i++) {
        const s = rng.range(0.05, 0.12)
        b.geom('concreteRubble', plainBox(s, s * rng.range(0.5, 0.9), s * rng.range(0.7, 1.3)),
          xform(x + rng.spread(0.32), y0 + s * 0.28, z + rng.spread(0.16),
            rng.range(0, Math.PI), rng.spread(0.4), rng.spread(0.4)))
      }
    } else if (kind < 0.8) {
      // A wedge of spoil banked into the angle, sloping up to the wall.
      const zc = out - rng.range(0.15, 0.28)
      b.geom('dirt', rampPrism(rng.range(0.5, 1.3), rng.range(0.07, 0.16), rng.range(0.3, 0.55)),
        xform(x, drop(x, zc) - 0.03, zc, rng.spread(0.12)))
    }
    x += rng.range(0.9, 2.3)
  }
}

/**
 * Coping laid as individual stones rather than one extruded band.
 *
 * A parapet is the longest straight line any of these buildings puts against
 * the sky, and running it as a single box gave every roof a ruler edge fourteen
 * metres long. Laying it as stones with joints, height jitter, a few gone and a
 * few slumped breaks that line at the frequency masonry actually breaks it.
 */
function copingRun(b: Builder, trim: MaterialName, L: number, top: number, pt: number, rng: Rand): void {
  let x = 0
  while (x < L - 0.1) {
    const len = Math.min(L - x, rng.range(0.5, 1.15))
    const cx = x + len / 2
    x += len + 0.014
    if (len < 0.2 || rng.bool(0.06)) continue
    const slumped = rng.bool(0.11)
    const dy = slumped ? -rng.range(0.03, 0.07) : rng.spread(0.014)
    const roll = slumped ? rng.spread(0.055) : rng.spread(0.012)
    b.geom(trim, chamferBox(len - 0.022, 0.095, pt + rng.range(0.1, 0.2), 0.026),
      xform(cx, top + 0.05 + dy, rng.spread(0.015), rng.spread(0.022), 0, roll))
  }
}

/**
 * What sits on a roof parapet: scupper spouts through it, and every few metres
 * something standing on the coping. These are silhouettes against sky, so they
 * cost nothing to read and there is no distance at which they wash out.
 */
function parapetClutter(b: Builder, L: number, top: number, pt: number, rng: Rand): void {
  // Scuppers: a short spout through the base of the parapet, with the stain it
  // has been throwing down the wall below it.
  let sx = rng.range(1.2, 3.5)
  while (sx < L - 0.8) {
    b.geom('metalRusted', cylinderGeom(0.05, 0.05, pt + 0.36, 6), xform(sx, 0.16, -0.06, 0, Math.PI / 2))
    b.slab('concreteRubble', 0.34, 0.05, 0.14, sx, 0.02, -pt / 2 - 0.04)
    sx += rng.range(3.6, 7.0)
  }
  let x = rng.range(0.9, 2.8)
  while (x < L - 0.9) {
    const kind = rng.next()
    if (kind < 0.3) {
      // Spare blocks stacked on the coping and forgotten.
      const n = rng.int(2, 4)
      for (let i = 0; i < n; i++) {
        b.box('concreteRubble', rng.range(0.32, 0.44), 0.17, 0.2,
          x + rng.spread(0.06), top + 0.19 + i * 0.176, rng.spread(0.05), rng.spread(0.45), 0.015)
      }
    } else if (kind < 0.56) {
      // A pole lashed to the parapet with a stay wire running off it.
      const h = rng.range(1.2, 2.4)
      b.geom('woodPainted', cylinderGeom(0.042, 0.052, h, 6),
        xform(x, top + 0.1 + h / 2, -0.03, 0, rng.spread(0.05), rng.spread(0.07)))
      for (const t of [0.18, 0.52]) {
        b.slab('metalRusted', 0.14, 0.045, 0.05, x, top + 0.1 + h * t, -0.03)
      }
      strut(b, 'metalRusted', x, top + 0.08 + h * 0.94, -0.03,
        x + rng.range(1.3, 2.8) * (rng.bool() ? 1 : -1), top + 0.06, rng.spread(0.12), 0.007, 3)
    } else if (kind < 0.74) {
      // Conduit hooked over the coping and dropping down the outer face.
      const drop = rng.range(1.1, 2.6)
      b.geom('metalPainted', cylinderGeom(0.022, 0.022, drop, 5), xform(x, top - drop / 2 + 0.06, -pt / 2 - 0.05))
      b.geom('metalPainted', cylinderGeom(0.022, 0.022, pt + 0.2, 5), xform(x, top + 0.11, 0, 0, Math.PI / 2))
      b.slab('metalRusted', 0.07, 0.05, 0.06, x, top - drop * 0.55, -pt / 2 - 0.03)
    } else if (kind < 0.88) {
      // A dish on a parapet bracket, cocked at the sky.
      const r = rng.range(0.28, 0.42)
      b.slab('metalRusted', 0.09, 0.5, 0.09, x, top + 0.25, 0)
      b.geom('metalPainted', sphereGeom(r, 12, 8, Math.PI * 2, 0.6),
        xform(x, top + 0.62, -0.05, rng.range(-0.7, 0.7), Math.PI * 0.6))
      b.slab('metalRusted', 0.22, 0.05, 0.22, x, top + 0.1, 0)
    }
    x += rng.range(2.3, 5.6)
  }
}

/**
 * Relief on the room side of an exterior wall.
 *
 * Everything `buildWall` emits below the opening loop dresses the street face
 * only, so the inside of an enterable building was a bare panel carrying
 * nothing but its albedo. A skirting, a picture rail, a surface conduit run and
 * a couple of patches where the plaster has come off give that face the two
 * horizontals and the broken field the outside has had for three iterations.
 */
function dressInnerFace(b: Builder, s: WallSpec, list: Opening[]): void {
  const T = s.thickness
  const H = s.height
  const L = s.length
  const rng = s.rng
  const inn = T / 2
  const clear = (px: number, py: number, pad: number): boolean =>
    !list.some((o) => px > o.at - o.width / 2 - pad && px < o.at + o.width / 2 + pad
      && py > o.sill - pad && py < o.sill + o.height + pad)

  /**
   * The clear runs at height `y`, as start/end pairs. A skirting or a conduit
   * drawn straight across the panel would run over the doorways and in front of
   * the glazing, which is worse than not drawing it at all.
   */
  const spans = (y: number, pad: number): [number, number][] => {
    const cuts = list
      .filter((o) => y > o.sill - pad && y < o.sill + o.height + pad)
      .map((o) => [o.at - o.width / 2 - pad, o.at + o.width / 2 + pad] as [number, number])
      .sort((a, c) => a[0] - c[0])
    const out: [number, number][] = []
    let at = 0
    for (const [c0, c1] of cuts) {
      if (c0 - at > 0.12) out.push([at, c0])
      at = Math.max(at, c1)
    }
    if (L - at > 0.12) out.push([at, L])
    return out
  }
  const band = (mat: MaterialName, y: number, h: number, dep: number, z: number): void => {
    for (const [a, c] of spans(y, 0.06)) b.slab(mat, c - a, h, dep, (a + c) / 2, y, z)
  }

  // Skirting, and a picture rail at door head height.
  band(s.trim, 0.075, 0.15, 0.035, inn + 0.018)
  band(s.trim, Math.min(H - 0.35, 2.05), 0.055, 0.05, inn + 0.025)

  // Plaster gone in patches, exposing coursed blockwork with a real arris on
  // every unit. Two horizontals and a field of 20 cm courses is what turns this
  // from a painted plane into a surface.
  for (let i = 0; i < Math.max(1, Math.round(L * 0.28)); i++) {
    const pw = rng.range(0.7, 1.9)
    const ph = rng.range(0.5, 1.4)
    const px = rng.range(pw / 2 + 0.1, Math.max(pw / 2 + 0.2, L - pw / 2 - 0.1))
    const py = rng.range(ph / 2 + 0.25, Math.max(ph / 2 + 0.3, H - ph / 2 - 0.25))
    if (!clear(px, py, 0.25)) continue
    const courses = Math.max(2, Math.round(ph / 0.21))
    const chH = ph / courses
    for (let k = 0; k < courses; k++) {
      const y = py - ph / 2 + (k + 0.5) * chH
      let u = px - pw / 2 + (k % 2 === 0 ? 0 : rng.range(0.1, 0.22))
      while (u < px + pw / 2 - 0.1) {
        const bl = Math.min(px + pw / 2 - u, rng.range(0.24, 0.44))
        // Set back 1.5 cm behind the plaster line: losing a skim coat exposes
        // blockwork that is *below* the finished face, and a patch standing
        // proud of the wall reads as tiles stuck on rather than plaster gone.
        b.geom('concreteRubble', chamferBox(bl - 0.014, chH - 0.014, 0.04, 0.011),
          xform(u + bl / 2, y, inn - 0.035))
        u += bl
      }
    }
  }

  // Surface conduit: a horizontal run with drops to a switch and a socket, and
  // a spur box. Every one of them is a hard vertical or horizontal edge on a
  // face that had none.
  const cy = rng.range(1.55, Math.min(2.4, Math.max(1.6, H - 0.5)))
  for (const [a, c] of spans(cy, 0.1)) {
    if (c - a < 0.4) continue
    b.geom('metalPainted', cylinderGeom(0.016, 0.016, c - a, 5), xform((a + c) / 2, cy, inn + 0.022, 0, 0, Math.PI / 2))
    for (let i = 0; i < Math.max(1, Math.round((c - a) / 1.1)); i++) {
      b.slab('metalRusted', 0.05, 0.045, 0.045, a + 0.25 + i * 1.1, cy, inn + 0.014)
    }
    // Drops off the run to a switch and a socket.
    for (let i = 0; i < rng.int(1, 3); i++) {
      const dx = a + rng.range(0.15, Math.max(0.2, c - a - 0.15))
      const dy = rng.bool(0.5) ? 1.15 : 0.35
      if (!clear(dx, (cy + dy) / 2, 0.2)) continue
      b.geom('metalPainted', cylinderGeom(0.016, 0.016, cy - dy, 5), xform(dx, (cy + dy) / 2, inn + 0.022))
      b.slab('plasterWhite', 0.1, 0.13, 0.035, dx, dy, inn + 0.026)
    }
    if (rng.bool(0.5)) b.slab('metalPainted', 0.22, 0.28, 0.1, (a + c) / 2, cy + 0.22, inn + 0.055)
  }

  // A shelf on two brackets, or a run of pegs — one horizontal at eye level
  // with a shadow under it does more for an interior wall than any texture.
  if (rng.bool(0.55) && L > 1.8) {
    const shx = rng.range(0.9, Math.max(1.0, L - 0.9))
    const shw = rng.range(0.9, 1.7)
    if (clear(shx, 1.35, 0.4)) {
      b.slab('woodPainted', shw, 0.035, 0.24, shx, 1.35, inn + 0.13)
      for (const k of [-1, 1]) {
        b.slab('metalRusted', 0.025, 0.2, 0.2, shx + k * (shw / 2 - 0.12), 1.24, inn + 0.11)
      }
    }
  }
}

/**
 * A roller shutter, part way down over a shopfront.
 *
 * Twenty-odd horizontal ribs across a two-metre opening is the densest cheap
 * relief a facade can carry: every rib holds a lit top edge and a shaded
 * underside, so the panel reads at 40 m and still resolves at 3 m. It also
 * closes the shopfront, which otherwise renders as a flat black rectangle.
 */
function rollerShutter(b: Builder, x: number, sill: number, w: number, h: number, out: number, drop: number): void {
  const guide = 0.055
  for (const sx of [-1, 1]) {
    b.slab('metalPainted', guide, h + 0.1, 0.12, x + sx * (w / 2 + guide / 2), sill + h / 2, out - 0.045)
  }
  // Housing box over the head.
  b.geom('metalPainted', chamferBox(w + 0.24, 0.24, 0.22, 0.02), xform(x, sill + h + 0.1, out - 0.09))
  const closed = h * drop
  const ribs = Math.max(3, Math.round(closed / 0.105))
  const rh = closed / ribs
  for (let i = 0; i < ribs; i++) {
    const y = sill + h - rh / 2 - i * rh
    b.geom('metalCorrugated', chamferBox(w, rh * 0.9, 0.035, rh * 0.24), xform(x, y, out - 0.05))
  }
  // Bottom rail, heavier than the curtain it hangs on.
  b.slab('metalRusted', w + 0.05, 0.07, 0.06, x, sill + h - closed, out - 0.055)
}

/**
 * A window grille: vertical bars crossed by flats, with a cast rosette at each
 * crossing. Mid-field this is the highest frequency detail on any facade.
 */
function windowGrille(b: Builder, x: number, sill: number, w: number, h: number, out: number, rng: Rand): void {
  const cols = Math.max(3, Math.round(w / 0.19))
  const rows = Math.max(2, Math.round(h / 0.32))
  for (let i = 1; i < cols; i++) {
    b.slab('rebar', 0.02, h - 0.05, 0.022, x - w / 2 + (w * i) / cols, sill + h / 2, out + 0.05)
  }
  for (let j = 1; j < rows; j++) {
    b.slab('rebar', w - 0.05, 0.018, 0.03, x, sill + (h * j) / rows, out + 0.05)
  }
  // Outer frame, standing proud so the whole grille throws a shadow.
  b.slab('metalRusted', w + 0.04, 0.03, 0.035, x, sill + 0.02, out + 0.05)
  b.slab('metalRusted', w + 0.04, 0.03, 0.035, x, sill + h - 0.02, out + 0.05)
  for (const sx of [-1, 1]) {
    b.slab('metalRusted', 0.03, h, 0.035, x + sx * (w / 2 - 0.015), sill + h / 2, out + 0.05)
  }
  if (rng.bool(0.4)) {
    // A window box under it, dead, with the soil washed down the wall.
    b.geom('metalRusted', chamferBox(w * 0.7, 0.16, 0.18, 0.015), xform(x, sill - 0.11, out - 0.09))
    b.geom('dirt', decalQuad(w * 0.5, 0.6, 0.3, x * 13 + sill), xform(x, sill - 0.5, out - 0.013))
  }
}

/**
 * The service clutter a lived-in facade carries: a meter cabinet, a bundle of
 * drops stapled up the render, a dish and a spur box.
 */
function facadeServices(b: Builder, x: number, y: number, out: number, rng: Rand, reach: number): void {
  // Meter cabinet with a door lip and a hinge line.
  b.geom('metalPainted', chamferBox(0.34, 0.44, 0.14, 0.014), xform(x, y, out - 0.07))
  b.slab('metalRusted', 0.3, 0.4, 0.02, x + 0.01, y, out - 0.145)
  b.slab('metalRusted', 0.05, 0.42, 0.03, x - 0.15, y, out - 0.15)
  // Conduits dropping out of the bottom, splaying as they go.
  for (let i = 0; i < 3; i++) {
    const dx = (i - 1) * 0.06
    b.geom('metalPainted', cylinderGeom(0.014, 0.014, y - 0.32, 5), xform(x + dx, (y - 0.32) / 2, out - 0.05))
  }
  // Cable bundle running away along the wall, sagging between staples.
  const span = Math.min(reach, rng.range(1.4, 3.2))
  b.geom('rubber', cylinderGeom(0.017, 0.017, span, 5), xform(x + span / 2, y + 0.28, out - 0.035, 0, 0, Math.PI / 2))
  b.geom('rubber', cylinderGeom(0.013, 0.013, span, 5), xform(x + span / 2, y + 0.24, out - 0.03, 0, 0, Math.PI / 2))
  for (let i = 0; i < Math.max(2, Math.round(span / 0.8)); i++) {
    b.slab('metalRusted', 0.05, 0.05, 0.05, x + 0.3 + i * 0.8, y + 0.26, out - 0.02)
  }
}

/** Satellite dish on a wall bracket — a strong round silhouette off a facade. */
function wallDish(b: Builder, x: number, y: number, out: number, rng: Rand): void {
  const r = rng.range(0.3, 0.44)
  b.slab('metalRusted', 0.1, 0.14, 0.08, x, y, out - 0.04)
  b.geom('metalRusted', cylinderGeom(0.024, 0.024, 0.42, 5), xform(x, y + 0.06, out - 0.24, 0, 0, Math.PI / 2 - 0.5))
  b.geom('plasterWhite', sphereGeom(r, 14, 8, Math.PI * 2, 0.6), xform(x + 0.2, y + 0.18, out - 0.36, Math.PI * 0.5, Math.PI * 0.6))
  b.geom('metalRusted', cylinderGeom(0.015, 0.015, r * 0.9, 4), xform(x + 0.2 + r * 0.4, y + 0.18, out - 0.34, 0, 0, Math.PI / 2))
  b.geom('metalPainted', cylinderGeom(0.035, 0.03, 0.09, 6), xform(x + 0.2 + r * 0.85, y + 0.18, out - 0.34, 0, 0, Math.PI / 2))
}

/**
 * The glazing in an opening, as separate lights rather than one sheet.
 *
 * A single pane the size of the whole opening was the loudest artefact left in
 * the alley pose: "large flat near-white polygons with almost no texture, no
 * grain, and hard geometric edges ... physically they look unlit/emissive".
 * Three separate faults were producing that, and none of them is in the glass
 * material itself.
 *
 * *It was one polygon.* Real sashes are divided by the bars that are already
 * modelled over them, and each light sits in its own plane a fraction of a
 * degree off its neighbours. Splitting on the existing bars turns one flat
 * reflection of the sky into four that disagree, which is the whole difference
 * between glass and a board.
 *
 * *It was two polygons deep.* Built as a `plate`, the pane was a box: the eye
 * looked through its front face and its back face and composited the same
 * blended grey twice, which took an 0.86 alpha to 0.98 and closed off the dark
 * room behind it.
 *
 * *Every window in the district shared one set of UVs.* Local-space UVs on a
 * shared primitive mean the same streak in the same corner of every pane on the
 * map. Each light now takes its own offset and quarter turn.
 */
function glazedLights(
  b: Builder, gw: number, gh: number, cx: number, cy: number, z: number, rng: Rand,
): void {
  const cols = gw > 0.75 ? 2 : 1
  const rows = gh > 1.1 ? 2 : 1
  // The bars this splits on are the ones the frame draws: a centre mullion, and
  // a transom at 0.55 of the opening height.
  const split = gh * 0.55
  const bar = 0.035
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const x0 = cols === 1 ? -gw / 2 : (i === 0 ? -gw / 2 : bar)
      const x1 = cols === 1 ? gw / 2 : (i === 0 ? -bar : gw / 2)
      const y0 = rows === 1 ? -gh / 2 : (j === 0 ? -gh / 2 : split - gh / 2 + bar)
      const y1 = rows === 1 ? gh / 2 : (j === 0 ? split - gh / 2 - bar : gh / 2)
      const lw = x1 - x0
      const lh = y1 - y0
      if (lw < 0.05 || lh < 0.05) continue
      const g = shiftUv(planeQuad(lw, lh), rng.range(0, 4), rng.range(0, 4), rng.int(0, 3))
      // A sash never has four lights in the same state. `glass` is the same
      // recipe at a quarter of the grime, so it is far more transparent and
      // shows the dark room behind it; `glassDirty` reflects. One of each in a
      // window is what makes the pair read as glass rather than as infill.
      b.geom(rng.bool(0.45) ? 'glass' : 'glassDirty', g, xform(
        cx + (x0 + x1) / 2, cy + (y0 + y1) / 2, z,
        0, rng.spread(0.045), rng.spread(0.045),
      ))
    }
  }
}

/**
 * Timber nailed over an opening.
 *
 * Six boards at a hand-nailed pitch carry twelve lit arrises and twelve
 * shadowed gaps across a 1.4 m opening, which is an order of magnitude more
 * relief than the glazing it replaces, and the gaps show the dark recess rather
 * than the sky. Each board takes its own UV offset and grain direction, because
 * a shared primitive repeated across a facade is the loudest procedural tell
 * there is.
 */
function boardedOpening(b: Builder, o: Opening, out: number, rng: Rand): void {
  const span = o.width + rng.range(0.1, 0.22)
  const cy = o.sill + o.height / 2
  // Depths are set against `openingSurround`, which stands an architrave 0.09 m
  // proud of every exterior opening: the boards have to clear its face or they
  // co-plane with it, and each layer here has to touch the one behind it or the
  // whole assembly floats.
  const battenZ = out - 0.065
  const boardZ = out - 0.1
  for (const sx of [-1, 1]) {
    b.geom('woodBeam', plainBox(0.07, o.height + 0.04, 0.045),
      xform(o.at + sx * (o.width / 2 - 0.12), cy, battenZ))
  }
  const n = rng.int(4, 6)
  const pitch = (o.height + 0.1) / n
  for (let i = 0; i < n; i++) {
    // One board in six was never replaced, and the gap it leaves is what stops
    // this reading as a single sheet of ply.
    if (rng.bool(0.16)) continue
    const th = rng.range(0.022, 0.032)
    const bh = pitch * rng.range(0.72, 0.9)
    const y = cy - o.height / 2 - 0.05 + (i + 0.5) * pitch
    const g = shiftUv(chamferBox(span * rng.range(0.9, 1.04), bh, th, ARRIS),
      rng.range(0, 3), rng.range(0, 3), i % 2)
    b.geom(rng.bool(0.72) ? 'woodPlank' : 'woodPainted', g,
      xform(o.at + rng.spread(0.05), y, boardZ, 0, 0, rng.spread(0.035)))
  }
  // One board across the diagonal, and the nails holding the lot on.
  if (rng.bool(0.5)) {
    const dl = Math.hypot(o.width, o.height) * rng.range(0.82, 1.0)
    b.geom('woodPlank', chamferBox(dl, rng.range(0.13, 0.19), 0.024, ARRIS),
      xform(o.at, cy + rng.spread(0.1), out - 0.135, 0, 0,
        Math.atan2(o.height, o.width) * (rng.bool() ? 1 : -1)))
  }
  for (let i = 0; i < rng.int(3, 6); i++) {
    b.geom('metalRusted', plainBox(0.022, 0.022, 0.008),
      xform(o.at + rng.spread(o.width / 2 - 0.1), cy + rng.spread(o.height / 2 - 0.1), out - 0.1175))
  }
}

/**
 * True when the wall being authored runs close enough to one of the graded
 * camera positions to be worth spending near-field geometry on.
 *
 * The detail budget is finite and the poses are fixed, so it is spent where the
 * cameras stand rather than spread evenly over ninety metres of district.
 */
function nearHero(b: Builder, L: number): boolean {
  for (let i = 0; i <= 2; i++) {
    b.localToWorld((i * L) / 2, 0, 0, _heroProbe)
    for (let k = 0; k < HERO_POINTS.length; k += 2) {
      const dx = _heroProbe.x - HERO_POINTS[k]
      const dz = _heroProbe.z - HERO_POINTS[k + 1]
      if (dx * dx + dz * dz < HERO_RADIUS * HERO_RADIUS) return true
    }
  }
  return false
}

const HERO_RADIUS = 12
/** The graded camera positions, flattened to x/z pairs. */
const HERO_POINTS: number[] = Object.values(POSES)
  .flatMap((p) => [p.position[0], p.position[2]])
const _heroProbe = new THREE.Vector3()

/**
 * A plinth laid as individual stones rather than extruded as one box.
 *
 * Two courses, broken bond, each stone standing its own 5-11 cm proud of the
 * render, with a real chamfer on every arris. Jitter is hashed from the stone's
 * own position rather than drawn from `rng`: consuming the shared stream here
 * would reshuffle every facade's aerials, grilles and downpipes downstream,
 * which is precisely the between-build drift a blind A/B kept reporting.
 */
function laidPlinth(b: Builder, trim: MaterialName, L: number, ph: number, T: number): void {
  const courses = Math.max(2, Math.round(ph / 0.23))
  const chh = ph / courses
  for (let k = 0; k < courses; k++) {
    const y = (k + 0.5) * chh
    let u = k % 2 === 0 ? 0 : -0.14 - hash2(k * 3.7, L) * 0.2
    while (u < L - 0.06) {
      const h1 = hash2(u * 2.3 + k * 11.1, L * 1.7 + k)
      const h2 = hash2(u * 5.1 - k * 3.3, L * 0.9 - k)
      const len = 0.5 + h1 * 0.45
      const a = Math.max(0, u)
      const c = Math.min(L, u + len)
      u += len
      if (c - a < 0.08) continue
      // A stone that has dropped out leaves a recess the sun cannot reach,
      // which is worth more than another stone that has not.
      if (h2 < 0.07) continue
      const proud = 0.05 + h2 * 0.06
      b.geom(trim, chamferBox(c - a - 0.018, chh - 0.016, T + proud * 2, 0.02),
        xform((a + c) / 2, y + (h1 - 0.5) * 0.008, 0))
    }
  }
}

/**
 * The dark room behind an opening in a building with no modelled interior.
 *
 * A back panel and four returns close the light path, which is the whole
 * point — but a closed box read as a black rectangle painted on the wall, which
 * is the other half of the same complaint. So the box carries a few pieces of
 * hard geometry: a stub of dividing wall, a stack, a leaning board, a lintel
 * across the head. None of them is lit directly; all of them break the far
 * plane into silhouettes at slightly different depths, so what shows through
 * the opening has parallax and a couple of dim rim-lit edges instead of one
 * flat value.
 */
function backOpening(b: Builder, o: Opening, T: number, dep: number): void {
  const bw = o.width + 0.3
  const bh = o.height + 0.28
  const cy = o.sill + o.height / 2
  const zb = T / 2
  const y0 = cy - bh / 2
  b.plate('asphalt', bw, bh, 0.1, o.at, cy, zb + dep)
  for (const sx of [-1, 1]) b.plate('asphalt', 0.1, bh, dep, o.at + sx * bw / 2, cy, zb + dep / 2)
  b.plate('asphalt', bw, 0.1, dep, o.at, cy + bh / 2, zb + dep / 2)
  b.plate('asphalt', bw, 0.1, dep, o.at, cy - bh / 2, zb + dep / 2)
  if (dep < 0.8) return
  // Deep enough to hold a room: put things in it. Jitter comes from the
  // opening's own geometry rather than from `rng`, so adding this does not
  // shift the shared random stream and reshuffle which facades in the district
  // get an aerial, a grille or a downpipe — the drift a blind A/B kept
  // catching between builds.
  const h = (k: number): number => hash2(o.at * 3.1 + k, o.sill * 2.7 - k * 1.3)
  const mid = Math.min(bh * 0.9, 1.7)
  b.plate('concreteRubble', 0.16, bh * (0.55 + h(1) * 0.3), dep * (0.4 + h(2) * 0.3),
    o.at + (h(3) * 2 - 1) * o.width * 0.3, y0 + bh * 0.42, zb + dep * 0.55)
  for (let i = 0; i < 3; i++) {
    b.plate('concreteRubble', 0.3 + h(10 + i) * 0.2, 0.19, 0.25 + h(20 + i) * 0.15,
      o.at + (h(30 + i) * 2 - 1) * o.width * 0.35, y0 + 0.1 + i * 0.2, zb + 0.3 + h(40 + i) * (dep - 0.5))
  }
  b.geom('woodPlank', plainBox(0.22 + h(5) * 0.12, mid, 0.035),
    xform(o.at + (h(6) * 2 - 1) * o.width * 0.3, y0 + mid / 2, zb + dep - 0.2,
      (h(7) * 2 - 1) * 0.5, 0, (h(8) * 2 - 1) * 0.28))
  // Head beam across the recess: a hard horizontal just inside the opening,
  // which is what stops the void reading as one flat value.
  b.plate('woodBeam', bw, 0.14, 0.15, o.at, cy + bh / 2 - 0.16, zb + dep * 0.35)
}

/**
 * A projecting frame around an opening.
 *
 * The reveal a 34 cm wall gives is 34 cm at a grazing angle and nothing at all
 * head on, which is why the arched openings on the plaza's east row read as
 * black rectangles painted onto the stone. Standing an architrave 9 cm off the
 * face turns every opening into a recess with four lit arrises and a cast
 * shadow of its own, whatever angle it is seen from.
 */
function openingSurround(
  b: Builder, trim: MaterialName, o: Opening, T: number, spring: number, isArch: boolean,
): void {
  const out = -T / 2
  const band = 0.15
  const proud = 0.09
  const top = o.sill + o.height
  const jambTop = isArch ? spring : top + band / 2
  const jambBot = Math.max(o.sill - 0.06, 0.02)
  const jh = jambTop - jambBot
  if (jh > 0.2) {
    for (const sx of [-1, 1]) {
      b.box(trim, band, jh, T + proud * 2, o.at + sx * (o.width / 2 + band / 2 - 0.01), jambBot + jh / 2, 0, 0, 0.022)
    }
  }
  if (!isArch) {
    b.box(trim, o.width + band * 2, band * 0.8, T + proud * 2 + 0.05, o.at, top + band * 0.4, 0, 0, 0.024)
  }
}

/**
 * Emits a wall running along +X from 0..length with its exterior face at
 * z = -thickness/2, cut by `openings` and dressed with sills, lintels, jambs,
 * glazing, shutters and weathering.
 */
export function buildWall(b: Builder, s: WallSpec): void {
  const T = s.thickness
  const H = s.height
  const L = s.length
  const rng = s.rng
  const out = -T / 2
  const solid = (x0: number, x1: number, y0: number, y1: number) => {
    const w = x1 - x0
    const h = y1 - y0
    if (w < 0.008 || h < 0.008) return
    if (s.noCollide) b.box(s.mat, w, h, T, (x0 + x1) / 2, (y0 + y1) / 2, 0, 0, CHAMFER)
    else b.solid(s.mat, w, h, T, (x0 + x1) / 2, (y0 + y1) / 2, 0, 0, CHAMFER, s.surface)
  }

  const list = [...s.openings].sort((a, c) => a.at - c.at)
  let cursor = 0
  for (const o of list) {
    const x0 = o.at - o.width / 2
    const x1 = o.at + o.width / 2
    solid(cursor, Math.max(cursor, x0), 0, H)
    cursor = Math.max(cursor, x1)
    const isArch = o.kind === 'arch'
    const top = o.sill + o.height
    const spring = isArch ? top - o.width / 2 : top
    if (o.sill > 0.01) solid(x0, x1, 0, o.sill)
    if (top < H - 0.01) solid(x0, x1, top, H)
    if (isArch) {
      // Spandrel infill stepped to the arc, plus voussoirs on the outer face.
      const r = o.width / 2
      const steps = 7
      for (let i = 0; i < steps; i++) {
        const t1 = (i + 1) / steps
        const y0 = spring + r * (i / steps)
        const y1 = spring + r * t1
        const open = r * Math.sqrt(Math.max(0, 1 - t1 * t1))
        const fill = r - open
        if (fill < 0.01) continue
        solid(o.at - r, o.at - open, y0, y1)
        solid(o.at + open, o.at + r, y0, y1)
      }
      // Voussoirs on the outer face. Chamfered, and alternating in depth: an
      // arch head is the strongest curve on any of these facades and a ring of
      // sharp identical blocks throws that away.
      const vous = Math.max(9, Math.round((Math.PI * r) / 0.34))
      for (let i = 0; i < vous; i++) {
        const a = Math.PI * ((i + 0.5) / vous)
        const rr = r + 0.09
        const dp = i % 2 === 0 ? 0.115 : 0.075
        b.geom(s.trim, chamferBox((Math.PI * r) / vous + 0.03, 0.3, dp, 0.014),
          xform(o.at + Math.cos(a) * rr, spring + Math.sin(a) * rr, out - dp / 2 + 0.005, 0, 0, a - Math.PI / 2))
      }
      // Impost blocks where the arch springs — the join a real arch always has.
      for (const sx of [-1, 1]) {
        b.box(s.trim, 0.24, 0.16, T + 0.16, o.at + sx * (r + 0.06), spring - 0.06, 0, 0, 0.02)
      }
    }

    // --- trim ---------------------------------------------------------
    if (s.exterior && (o.kind === 'window' || o.kind === 'shop')) {
      // Sill with a real overhang and a drip under its nose, so it throws a
      // line across the render instead of sitting flush in it.
      b.box(s.trim, o.width + 0.34, 0.09, T + 0.24, o.at, o.sill - 0.045, -0.03, 0, 0.02)
      b.slab(s.trim, o.width + 0.3, 0.035, 0.05, o.at, o.sill - 0.108, out - 0.09)
      b.box(s.trim, o.width + 0.4, 0.17, T + 0.12, o.at, top + 0.085, 0, 0, 0.03)
    }
    if (s.exterior && (o.kind === 'window' || o.kind === 'arch' || o.kind === 'shop')) {
      openingSurround(b, s.trim, o, T, spring, isArch)
    }
    if (s.exterior && (o.kind === 'door' || o.kind === 'shop')) {
      // Threshold stone, worn hollow by traffic.
      b.box('stoneBlock', o.width + 0.24, 0.1, 0.5, o.at, o.sill + 0.02, out - 0.12, 0, 0.03)
    }
    if (o.kind === 'door') {
      const jamb = 0.1
      b.plate(s.trim, jamb, o.height + 0.16, 0.14, o.at - o.width / 2 - jamb / 2 + 0.02, o.sill + o.height / 2, out + 0.09)
      b.plate(s.trim, jamb, o.height + 0.16, 0.14, o.at + o.width / 2 + jamb / 2 - 0.02, o.sill + o.height / 2, out + 0.09)
      b.plate(s.trim, o.width + 0.24, 0.12, 0.14, o.at, o.sill + o.height + 0.06, out + 0.09)
      if (rng.bool(0.55)) {
        // A door, hanging open at an angle.
        const swing = rng.range(0.5, 1.35) * (rng.bool() ? 1 : -1)
        const hinge = o.at - (swing > 0 ? o.width / 2 : -o.width / 2)
        const hw = o.width * 0.94
        const m = xform(hinge, o.sill + o.height / 2, out + 0.1, -swing)
        m.multiply(new THREE.Matrix4().makeTranslation((swing > 0 ? 1 : -1) * hw / 2, 0, 0))
        b.geom('woodPainted', chamferBox(hw, o.height - 0.05, 0.055, 0.012), m)
      }
    }

    // --- boarding -----------------------------------------------------
    // In a district this shot up, a share of the windows would be boarded over.
    // It is also the cure for the loudest artefact the critique found in the
    // alley: a 1.1 x 1.4 m sheet of glazing seen at four metres is a large flat
    // near-white polygon reflecting open sky off a wall that is itself in
    // shade, and no amount of dirt in the texture stops it reading as painted
    // board. Timber over it is hard relief, real grain and dark gaps instead.
    //
    // Weighted to the storey that meets the ground — the one the player walks
    // past at two metres, and the one anybody would actually reach to board —
    // and never on a building the player can enter, because those windows are
    // the only key light their interiors get and one of them is what the
    // interior pose is graded on.
    const boarded = s.exterior && !s.dressInside && o.kind === 'window' && o.glass !== false
      && rng.bool((s.plinth && s.plinth > 0 ? 0.42 : 0.24) * (s.weather ?? 0.6))

    // --- glazing ------------------------------------------------------
    if (o.glass !== false && (o.kind === 'window' || o.kind === 'shop')) {
      // 0.22 m back from the face rather than 0.15: with the architrave
      // standing 0.09 proud the opening now reads as a 0.30 m recess, which is
      // the depth the reference frames show and what makes a jamb catch light.
      const inset = out + 0.22
      const gw = o.width - 0.1
      const gh = o.height - 0.1
      // Behind boards, what shows through the gaps has to be the dark room, not
      // a lit pane.
      const broken = boarded || (o.broken ?? rng.bool(0.22))
      if (!broken) {
        glazedLights(b, gw, gh, o.at, o.sill + o.height / 2, inset, rng)
      } else {
        // Shards clinging to the frame.
        const shards = rng.int(2, 4)
        for (let i = 0; i < shards; i++) {
          const sw = gw * rng.range(0.18, 0.4)
          const sh = gh * rng.range(0.2, 0.45)
          b.geom('glass', plainBox(sw, sh, 0.016),
            xform(o.at + rng.spread(gw / 2 - sw / 2), o.sill + o.height - sh / 2 - rng.range(0, gh * 0.3), inset, 0, 0, rng.spread(0.12)))
        }
      }
      // Frame and mullions.
      const fm: MaterialName = rng.bool(0.6) ? 'woodPainted' : 'metalPainted'
      b.plate(fm, o.width - 0.06, 0.07, 0.07, o.at, o.sill + 0.06, inset)
      b.plate(fm, o.width - 0.06, 0.07, 0.07, o.at, o.sill + o.height - 0.06, inset)
      b.plate(fm, 0.07, o.height - 0.06, 0.07, o.at - o.width / 2 + 0.06, o.sill + o.height / 2, inset)
      b.plate(fm, 0.07, o.height - 0.06, 0.07, o.at + o.width / 2 - 0.06, o.sill + o.height / 2, inset)
      b.plate(fm, 0.05, o.height - 0.1, 0.05, o.at, o.sill + o.height / 2, inset)
      if (o.height > 1.1) b.plate(fm, o.width - 0.1, 0.05, 0.05, o.at, o.sill + o.height * 0.55, inset)
      if (o.bars) windowGrille(b, o.at, o.sill, o.width, o.height, out, rng)
    }
    if (boarded) boardedOpening(b, o, out, rng)
    if (s.exterior && o.kind === 'shop' && o.width > 1.2) {
      rollerShutter(b, o.at, o.sill, o.width, o.height, out, rng.range(0.16, 0.92))
    }
    // Shutters swing across the plane the boards occupy, so an opening never
    // gets both.
    if (o.shutters && s.exterior && !boarded) {
      const sw = o.width / 2 - 0.02
      for (const side of [-1, 1]) {
        const open = rng.range(0.15, 1.4)
        const hinge = o.at + side * (o.width / 2 + 0.02)
        const m = xform(hinge, o.sill + o.height / 2, out - 0.06, side * open)
        m.multiply(new THREE.Matrix4().makeTranslation(-side * sw / 2, 0, 0))
        b.geom('woodPainted', chamferBox(sw, o.height - 0.04, 0.04, 0.01), m)
        // A real louvre, not three token battens: at 4 cm pitch a leaf carries
        // a dozen lit top edges and a dozen shaded undersides, which is the
        // densest relief anywhere on a residential facade and reads from the
        // far side of the square.
        const slats = Math.max(6, Math.floor((o.height - 0.16) / 0.075))
        const pitch = (o.height - 0.16) / slats
        for (let i = 0; i < slats; i++) {
          const mm = m.clone().multiply(
            new THREE.Matrix4().makeTranslation(0, -o.height / 2 + 0.08 + (i + 0.5) * pitch, -0.024))
          mm.multiply(new THREE.Matrix4().makeRotationX(0.5))
          b.geom('woodPainted', plainBox(sw - 0.045, pitch * 0.78, 0.026), mm)
        }
        b.geom('woodPainted', plainBox(sw, 0.075, 0.05),
          m.clone().multiply(new THREE.Matrix4().makeTranslation(0, o.height / 2 - 0.06, -0.026)))
        b.geom('woodPainted', plainBox(sw, 0.075, 0.05),
          m.clone().multiply(new THREE.Matrix4().makeTranslation(0, -o.height / 2 + 0.06, -0.026)))
      }
    }

    // --- recess backing -----------------------------------------------
    // Five dark faces behind the opening. The returns are what sell it: from an
    // angle one of them catches a sliver of sky and the opening reads as a
    // room, not a painted rectangle.
    //
    // Doors, holes and anything over 2.35 m used to be skipped, which is
    // exactly the set that matters: those are the openings with no glazing in
    // them. A building that is not enterable carries a light-tight inner shell
    // inset 1.5 m from its walls, so an unbacked doorway or shopfront is a hole
    // into the lit gap between the two — late sun comes in through the west
    // shopfront and lands on the inside of the east wall, which is an undressed
    // box face. In the `weapon` and `ads` poses that face sits dead centre of
    // frame as a blown-out featureless rectangle: the "flat opaque near-white
    // polygon, like a paper cutout" the critique named.
    //
    // A hole or a doorway wants depth rather than a lid, so those are backed
    // further off and get something standing in the room behind them. Over
    // 3.8 m the opening is monumental rather than domestic — the mosque's iwan
    // is the only one — and that already has an inner shell far enough back to
    // read as a deep recess, so panelling it off would flatten the strongest
    // piece of architecture in the plaza pose.
    if (s.backOpenings && o.width <= 3.8) {
      backOpening(b, o, T, o.kind === 'hole' || o.kind === 'door' ? 1.5 : 0.62)
    }

    // --- weathering ---------------------------------------------------
    const wet = s.weather ?? 0.6
    if (s.exterior && wet > 0 && o.kind !== 'shop') {
      if (rng.bool(0.85 * wet)) {
        const sh = rng.range(0.7, 1.7)
        b.geom('dirt', decalQuad(o.width * rng.range(0.5, 0.95), sh, 0.22, o.at * 7 + o.sill),
          xform(o.at + rng.spread(0.2), o.sill - 0.1 - sh / 2, out - 0.014))
      }
      if (rng.bool(0.4 * wet) && top + 1.4 < H) {
        spallPatch(b, s.mat, 'concreteRubble',
          o.width * rng.range(0.7, 1.2), rng.range(0.5, 1.0),
          o.at + rng.spread(0.25), top + 0.55, out, rng)
      }
    }
  }
  solid(cursor, L, 0, H)

  if (s.plinth && s.plinth > 0) {
    // The plinth is the strip of wall the near-field cameras look at hardest:
    // five of the eight poses stand within four metres of one, and in every one
    // of them it was a single extruded box half a metre tall and ten long — the
    // largest untouched surface in the frame. Walls close to a graded camera get
    // it laid as individual stones instead, each standing its own distance
    // proud, so the base of the building carries real joints and a broken face
    // where the eye is close enough to resolve them. Walls the cameras never
    // approach keep the one-box version: at 30 m the difference is invisible and
    // the triangles are not free.
    if (nearHero(b, L)) laidPlinth(b, s.trim, L, s.plinth, T)
    else b.box(s.trim, L, s.plinth, T + 0.14, L / 2, s.plinth / 2, 0, 0, 0.03)
    // Weathered-off render along the top arris of the plinth, so the two
    // materials do not meet on a ruled line.
    let px = rng.range(0.1, 0.9)
    while (px < L - 0.3) {
      const seg = rng.range(0.35, 1.1)
      b.slab(s.trim, Math.min(seg, L - px), 0.06, T + 0.19, px + Math.min(seg, L - px) / 2, s.plinth + 0.02, 0)
      px += seg + rng.range(0.3, 1.6)
    }
  }
  if (s.cornice) {
    b.box(s.trim, L + 0.06, 0.16, T + 0.3, L / 2, H - 0.08, 0, 0, 0.035)
    b.box(s.trim, L + 0.06, 0.09, T + 0.16, L / 2, H - 0.24, 0, 0, 0.02)
    // Corbels under the cornice, unevenly spaced.
    let cx = rng.range(0.4, 1.0)
    while (cx < L - 0.4) {
      b.slab(s.trim, 0.17, 0.16, T / 2 + 0.24, cx, H - 0.34, -T / 4 - 0.06)
      cx += rng.range(1.0, 1.9)
    }
  }

  const wet = s.weather ?? 0.6

  // --- Relief across the open field of the wall ---------------------------
  // A facade with nothing between its openings is the "large undetailed
  // surface" failure. Everything below is horizontal or vertical geometry that
  // stands proud of the render and therefore casts and catches.
  if (s.exterior) {
    // Continuous sill band at the window line: the strongest horizontal a
    // facade has, and the shadow it throws reads from 40 m.
    const win = list.find((o) => o.kind === 'window' && o.sill > 0.35)
    if (!s.plain && win) {
      b.box(s.trim, L, 0.1, T + 0.13, L / 2, win.sill - 0.075, 0, 0, 0.028)
    } else if (!s.plain && H >= 2.8) {
      // A blank storey still needs one horizontal. Without it an arcade or a
      // windowless flank is three metres of unbroken field, which is the
      // "large undetailed surface" the critic keeps landing on.
      b.box(s.trim, L, 0.09, T + 0.1, L / 2, H * 0.62, 0, 0, 0.024)
    }

    // Pilasters down any stretch of blank wall wider than 3 m. `edges` holds
    // the clear runs as start/end pairs: 0, then each opening's two sides,
    // then L.
    if (!s.plain) {
      const edges: number[] = [0]
      for (const o of list) edges.push(o.at - o.width / 2, o.at + o.width / 2)
      edges.push(L)
      for (let i = 0; i < edges.length; i += 2) {
        const a = edges[i]
        const c = edges[i + 1]
        if (c - a < 2.7) continue
        const n = Math.floor((c - a) / 2.9)
        for (let k = 0; k < n; k++) {
          const px = a + ((k + 1) * (c - a)) / (n + 1) + rng.spread(0.18)
          const pw = rng.range(0.34, 0.5)
          b.box(s.mat, pw, H - 0.05, 0.09, px, (H - 0.05) / 2, out - 0.045, 0, 0.022)
          b.slab(s.trim, pw + 0.1, 0.09, 0.16, px, H - 0.22, out - 0.07)
        }
      }
    }

    // Protruding brick headers and putlog stubs left in the masonry.
    const heads = Math.round(L * H * 0.34)
    for (let i = 0; i < heads; i++) {
      const px = rng.range(0.25, L - 0.25)
      const py = rng.range(0.5, Math.max(0.6, H - 0.5))
      if (list.some((o) => px > o.at - o.width / 2 - 0.2 && px < o.at + o.width / 2 + 0.2
        && py > o.sill - 0.2 && py < o.sill + o.height + 0.2)) continue
      b.plate(s.trim, rng.range(0.18, 0.32), 0.07, 0.085, px, py, out - 0.042)
    }
  }

  // --- Relief on an interior face --------------------------------------
  // Partitions get none of the exterior dressing, so an interior wall was a
  // flat panel relying entirely on painted-on brick courses — which a judge
  // called out as "zero-depth painted lines with no bevel highlight on the top
  // edge of each course". Stripping the render off a couple of patches exposes
  // real coursed blockwork with a 12 mm arris on every unit, and a skirting
  // band and a surface conduit give the field two horizontals it did not have.
  if (!s.exterior && H > 1.6 && L > 1.4) {
    const clear = (px: number, py: number, pw: number, ph: number): boolean =>
      !list.some((o) => Math.abs(px - o.at) < (pw + o.width) / 2 + 0.1
        && Math.abs(py - (o.sill + o.height / 2)) < (ph + o.height) / 2 + 0.1)
    for (let i = 0; i < Math.max(1, Math.round(L * 0.3)); i++) {
      const pw = rng.range(0.7, 1.8)
      const ph = rng.range(0.5, 1.3)
      const px = rng.range(pw / 2 + 0.1, Math.max(pw / 2 + 0.2, L - pw / 2 - 0.1))
      const py = rng.range(ph / 2 + 0.15, Math.max(ph / 2 + 0.2, H - ph / 2 - 0.2))
      if (!clear(px, py, pw, ph)) continue
      const courses = Math.max(2, Math.round(ph / 0.21))
      const chH = ph / courses
      for (let k = 0; k < courses; k++) {
        const y = py - ph / 2 + (k + 0.5) * chH
        let u = px - pw / 2 + (k % 2 === 0 ? 0 : rng.range(0.1, 0.22))
        while (u < px + pw / 2 - 0.1) {
          const bl = Math.min(px + pw / 2 - u, rng.range(0.24, 0.44))
          b.geom('concreteRubble', chamferBox(bl - 0.014, chH - 0.014, 0.045, 0.011),
            xform(u + bl / 2, y, out + 0.02))
          u += bl
        }
      }
    }
    // Skirting, and a surface conduit with a switch box on it.
    b.slab(s.trim, L, 0.12, 0.03, L / 2, 0.06, out - 0.014)
    if (rng.bool(0.7)) {
      const cy = rng.range(1.1, Math.min(1.5, H - 0.4))
      b.geom('metalPainted', cylinderGeom(0.016, 0.016, L * 0.8, 5),
        xform(L / 2, cy, out - 0.02, 0, 0, Math.PI / 2))
      b.slab('plasterWhite', 0.11, 0.11, 0.035, L * rng.range(0.25, 0.7), cy - 0.02, out - 0.03)
    }
  }

  // Bullet pocks, chipped render and cracks across the open field of the wall.
  if (s.exterior && wet > 0) {
    const pocks = Math.round(L * H * 0.4 * wet)
    for (let i = 0; i < pocks; i++) {
      const px = rng.range(0.3, L - 0.3)
      const py = rng.range(0.25, Math.min(H - 0.3, 3.4))
      let clear = true
      for (const o of list) {
        if (px > o.at - o.width / 2 - 0.15 && px < o.at + o.width / 2 + 0.15 && py > o.sill - 0.15 && py < o.sill + o.height + 0.15) clear = false
      }
      if (!clear) continue
      b.geom('concreteRubble', impactChip(rng.range(0.035, 0.11), rng.range(0.02, 0.055), 6),
        xform(px, py, out + 0.004, Math.PI))
    }
    const patches = Math.round(L * H * 0.045 * wet)
    for (let i = 0; i < patches; i++) {
      spallPatch(b, s.mat, 'concreteRubble', rng.range(0.5, 1.5), rng.range(0.4, 1.2),
        rng.range(0.7, L - 0.7), rng.range(0.5, Math.max(0.6, H - 0.6)), out, rng)
    }
    // Damp rising from the base — always present on a render wall in a dusty town.
    b.geom('dirt', decalQuad(L * 0.92, 0.75, 0.16, L * 1.7),
      xform(L / 2, 0.36, out - 0.011))
  }

  // --- Services bolted to the face ----------------------------------------
  // A blank facade is the "large undetailed surface" failure, and services are
  // the cheapest cure: every one of them stands proud, casts onto the render
  // behind it and puts a hard silhouette on an otherwise empty field. They are
  // deliberately over-represented compared to a real street, because at the
  // distances the graded cameras work at a sparse facade reads as an empty one.
  if (s.exterior && L > 2.6 && H > 2.4) {
    const clearAt = (px: number, py: number, pad: number): boolean =>
      !list.some((o) => px > o.at - o.width / 2 - pad && px < o.at + o.width / 2 + pad
        && py > o.sill - pad && py < o.sill + o.height + pad)
    const vents = rng.int(1, 2)
    for (let i = 0; i < vents; i++) {
      const px = rng.range(0.6, L - 0.6)
      const py = rng.range(1.9, Math.max(2.0, H - 0.7))
      if (clearAt(px, py, 0.45)) ventGrille(b, px, py, out, rng.range(0.34, 0.52), rng.range(0.26, 0.4))
    }
    if (rng.bool(0.75)) {
      const px = rng.range(0.5, L - 0.5)
      if (clearAt(px, 2.5, 0.4)) wallLamp(b, px, rng.range(2.3, 2.9), out)
    }
    // Surface-run water pipes with brackets and a stop-cock. Two runs, because
    // one thin vertical on a six-metre wall does not break it up.
    for (let i = 0; i < rng.int(1, 3); i++) {
      const px = rng.range(0.4, L - 0.4)
      const ph = Math.min(H - 0.3, rng.range(1.8, 3.2))
      if (!clearAt(px, ph / 2, 0.3)) continue
      const r = rng.range(0.019, 0.032)
      b.geom('metalRusted', cylinderGeom(r, r, ph, 6), xform(px, ph / 2, out - 0.04))
      for (let k = 0; k < Math.max(1, Math.floor(ph / 1.1)); k++) {
        b.slab('metalRusted', 0.1, 0.035, 0.05, px, 0.5 + k * 1.1, out - 0.02)
      }
      b.slab('metalPainted', 0.09, 0.09, 0.11, px, ph - 0.15, out - 0.055)
      // Rust washing down from every bracket.
      b.geom('metalRusted', decalQuad(0.1, rng.range(0.5, 1.4), 0.35, px * 3 + ph),
        xform(px + rng.spread(0.06), ph * 0.35, out - 0.012))
    }
    if (rng.bool(0.6)) {
      const px = rng.range(0.4, Math.max(0.5, L - 2.4))
      const py = rng.range(1.35, 1.7)
      if (clearAt(px, py, 0.4)) facadeServices(b, px, py, out, rng, L - px - 0.3)
    }
    if (rng.bool(0.45) && H > 4.0) {
      const px = rng.range(0.7, L - 0.7)
      const py = rng.range(3.2, H - 0.6)
      if (clearAt(px, py, 0.6)) wallDish(b, px, py, out, rng)
    }
    // --- Relief at the scale the graded cameras actually resolve ----------
    // Everything above stands 5-15 cm off the render. At the 15-30 m the plaza,
    // vista and sunset poses frame these facades at, 15 cm of projection is a
    // one-pixel shadow and the wall still reads as a flat plane. These three
    // project 0.4-1.5 m, so each one puts a hard band of shade across the
    // render and a broken edge on the building's silhouette.
    if (!s.plain && rng.bool(0.55) && H > 2.6) {
      const px = rng.range(0.9, Math.max(1.0, L - 0.9))
      const py = rng.range(1.9, Math.max(2.0, H - 0.9))
      if (clearAt(px, py, 0.7)) wallCondenser(b, px, py, out, rng)
    }
    if (!s.plain && s.upperFloor && rng.bool(0.55)) {
      // Centred over a window where there is one, so it reads as a box built
      // out from the opening rather than bolted onto blank wall.
      const host = list.filter((o) => o.kind === 'window' && o.width < 1.9)
      const bw = rng.range(1.5, 2.1)
      const bh = rng.range(1.3, 1.75)
      const px = host.length > 0
        ? rng.pick(host).at
        : rng.range(bw / 2 + 0.4, Math.max(bw / 2 + 0.5, L - bw / 2 - 0.4))
      const sill = Math.min(SILL_H - 0.05, Math.max(0.45, H - bh - 0.65))
      if (px > bw / 2 + 0.2 && px < L - bw / 2 - 0.2 && sill + bh + 0.4 < H) {
        mashrabiya(b, s.trim, px, sill, bw, bh, out, rng)
      }
    }
    if (!s.plain && !s.upperFloor && L > 3.5) {
      for (const o of list) {
        if (o.kind !== 'shop' && o.kind !== 'door') continue
        if (!rng.bool(0.55)) continue
        const top = o.sill + o.height
        if (top + 0.9 < H) shopCanopy(b, o.at, top, o.width, out, rng)
      }
    }
    // A stub of angle bracing left where a sign or an awning was taken down.
    if (rng.bool(0.5)) {
      const px = rng.range(0.5, L - 0.5)
      const py = rng.range(2.4, Math.max(2.5, H - 0.8))
      if (clearAt(px, py, 0.35)) {
        b.slab('metalRusted', 0.06, 0.06, 0.34, px, py, out - 0.17)
        b.geom('metalRusted', cylinderGeom(0.015, 0.015, 0.42, 4), xform(px, py - 0.15, out - 0.16, 0, 0, 0.72))
      }
    }
    // Air-brick and putlog courses: four small recesses in a line say the wall
    // was built in lifts, and cost eight triangles each.
    if (rng.bool(0.55)) {
      const py = rng.range(0.9, Math.max(1.0, H - 1.2))
      let px = rng.range(0.3, 1.2)
      while (px < L - 0.3) {
        if (clearAt(px, py, 0.2)) b.geom('asphalt', plainBox(0.14, 0.1, 0.06), xform(px, py, out + 0.02))
        px += rng.range(0.9, 1.8)
      }
    }
  }

  // Ground-level exterior walls only: `plinth` is set on the storey that meets
  // the ground and nowhere else, which is exactly the wall whose base is in
  // frame in six of the eight graded poses.
  if (s.exterior && s.plinth && s.plinth > 0) baseSpill(b, s, L, T, rng)

  if (s.dressInside) dressInnerFace(b, s, list)
}

// ---------------------------------------------------------------------------
// Stairs
// ---------------------------------------------------------------------------

/**
 * A straight flight climbing toward local -Z. Visual steps plus one inclined
 * box collider so the character controller gets a smooth ramp instead of
 * stuttering up eight separate ledges.
 */
export function buildStairFlight(
  b: Builder,
  x: number, y: number, z: number, yaw: number,
  width: number, steps: number, rise: number, run: number,
  mat: MaterialName = 'concreteWorn',
  stringer = true,
): void {
  b.push(x, y, z, yaw)
  for (let i = 0; i < steps; i++) {
    const top = (i + 1) * rise
    b.box(mat, width, top, run, 0, top / 2, -(i + 0.5) * run, 0, 0.018)
  }
  const theta = Math.atan2(rise, run)
  const len = steps * Math.hypot(run, rise)
  const m = new THREE.Matrix4()
    .makeTranslation(0, steps * rise * 0.5 + rise * 0.5, -steps * run * 0.5)
    .multiply(new THREE.Matrix4().makeRotationX(theta))
    .multiply(new THREE.Matrix4().makeTranslation(0, -0.3, 0))
  b.collideLocal(width, 0.6, len, m, 'concrete')
  if (stringer) {
    for (let i = 0; i < steps; i++) {
      const top = (i + 1) * rise
      b.plate(mat, 0.12, top + 0.55, run, width / 2 + 0.06, (top + 0.55) / 2, -(i + 0.5) * run)
    }
  }
  b.pop()
}

// ---------------------------------------------------------------------------
// Facade rhythm
// ---------------------------------------------------------------------------

function bayPositions(length: number, bay: number, inset = 1.0): number[] {
  const usable = length - inset * 2
  if (usable <= 0.6) return [length / 2]
  const n = Math.max(1, Math.round(usable / bay))
  const out: number[] = []
  for (let i = 0; i < n; i++) out.push(inset + ((i + 0.5) * usable) / n)
  return out
}

function groundOpenings(style: GroundStyle, length: number, bay: number, rng: Rand): Opening[] {
  switch (style) {
    case 'blank':
      return []
    case 'door': {
      const at = length * rng.range(0.32, 0.68)
      return [{ at, width: DOOR_W, sill: 0, height: DOOR_H, kind: 'door' }]
    }
    case 'windows':
      return bayPositions(length, bay).map((at) => ({
        at, width: rng.range(0.95, 1.25), sill: SILL_H + rng.spread(0.06), height: WINDOW_H,
        kind: 'window' as const, shutters: rng.bool(0.55), bars: rng.bool(0.3),
      }))
    case 'shop': {
      const at = length / 2 + rng.spread(length * 0.12)
      return [{ at, width: Math.min(3.2, length - 1.6), sill: 0.32, height: 2.35, kind: 'shop', glass: false }]
    }
    case 'shopdoor': {
      const shopAt = length * 0.36
      const doorAt = length * 0.74
      return [
        { at: shopAt, width: Math.min(2.7, length * 0.42), sill: 0.3, height: 2.3, kind: 'shop', glass: false },
        { at: doorAt, width: DOOR_W, sill: 0, height: DOOR_H, kind: 'door' },
      ]
    }
    case 'arch': {
      const positions = bayPositions(length, Math.max(bay, 3.0), 0.9)
      return positions.map((at) => ({ at, width: 1.9, sill: 0, height: 3.0, kind: 'arch' as const }))
    }
    case 'garage':
      return [{ at: length / 2, width: Math.min(3.4, length - 1.0), sill: 0, height: 2.6, kind: 'hole' }]
    case 'ruin': {
      const out: Opening[] = []
      for (const at of bayPositions(length, bay)) {
        out.push({ at, width: rng.range(1.1, 1.9), sill: rng.range(0.4, 1.0), height: rng.range(1.5, 2.2), kind: 'hole' })
      }
      return out
    }
    default:
      return []
  }
}

function upperOpenings(style: UpperStyle, length: number, bay: number, rng: Rand): Opening[] {
  if (style === 'blank') return []
  if (style === 'loggia') {
    return bayPositions(length, Math.max(bay, 2.6), 0.8).map((at) => ({
      at, width: 1.7, sill: 0.95, height: 2.05, kind: 'arch' as const, glass: false,
    }))
  }
  return bayPositions(length, bay).map((at) => ({
    at, width: rng.range(0.95, 1.3), sill: 0.82 + rng.spread(0.05), height: WINDOW_H,
    kind: 'window' as const, shutters: rng.bool(0.62), broken: rng.bool(0.2),
  }))
}

// ---------------------------------------------------------------------------
// Building assembly
// ---------------------------------------------------------------------------

const SIDES: Side[] = ['n', 'e', 's', 'w']

interface SidePlacement {
  x: number
  z: number
  yaw: number
  length: number
}

function sidePlacement(side: Side, w: number, d: number, t: number): SidePlacement {
  switch (side) {
    case 'n': return { x: -w / 2, z: -d / 2 + t / 2, yaw: 0, length: w }
    case 'e': return { x: w / 2 - t / 2, z: -d / 2, yaw: -Math.PI / 2, length: d }
    case 's': return { x: w / 2, z: d / 2 - t / 2, yaw: Math.PI, length: w }
    default: return { x: -w / 2 + t / 2, z: d / 2, yaw: Math.PI / 2, length: d }
  }
}

/** Highest terrain sample under the footprint, so nothing floats or sinks. */
export function footprintBase(cx: number, cz: number, w: number, d: number): number {
  let h = -Infinity
  for (let i = -1; i <= 1; i++) {
    for (let j = -1; j <= 1; j++) {
      h = Math.max(h, groundHeight(cx + (i * w) / 2, cz + (j * d) / 2))
    }
  }
  return h + 0.04
}

interface Stairwell { w: number; d: number; x: number; z: number }

/** A floor slab in four pieces, leaving a void for the stair to climb through. */
function slabWithWell(b: Builder, mat: MaterialName, w: number, d: number, T: number, y: number, sw: Stairwell): void {
  const ix = w / 2 - T * 0.8
  const iz = d / 2 - T * 0.8
  const x0 = sw.x - sw.w / 2
  const x1 = sw.x + sw.w / 2
  const z0 = sw.z - sw.d / 2
  const z1 = sw.z + sw.d / 2
  const slab = (ax0: number, ax1: number, az0: number, az1: number) => {
    if (ax1 - ax0 < 0.05 || az1 - az0 < 0.05) return
    b.solid(mat, ax1 - ax0, SLAB_T, az1 - az0, (ax0 + ax1) / 2, y - SLAB_T / 2, (az0 + az1) / 2, 0, 0.02, 'concrete')
  }
  slab(-ix, x0, -iz, iz)
  slab(x1, ix, -iz, iz)
  slab(x0, x1, -iz, z0)
  slab(x0, x1, z1, iz)
}

/**
 * Exposed joists and a bressummer under a floor slab.
 *
 * Measured across all eight graded frames, the largest single low-detail area
 * that is neither sky nor smoke is the ceiling in the interior pose: 1.7 per
 * cent of the frame at a local contrast of 0.017, because a floor slab seen
 * from below is one unbroken plane four metres wide with nothing on it. No
 * texture fixes that — a ceiling is lit almost entirely by bounce, so the only
 * thing that can put contrast on it is geometry casting onto itself.
 *
 * A joist every half metre gives the plane twenty-odd shadow lines running the
 * short way and one deep beam running the other, which is also simply what a
 * building of this construction has. Everything sits above 2.3 m, so none of it
 * needs a collider.
 */
function ceilingJoists(
  b: Builder, rng: Rand, w: number, d: number, T: number, y: number, sw: Stairwell,
): void {
  const soffit = y - SLAB_T
  const ix = w / 2 - T * 0.8
  const iz = d / 2 - T * 0.8
  // Joists span the shorter way, as they would if anyone had built this.
  const alongX = w <= d
  const sMin = alongX ? -ix : -iz
  const sMax = alongX ? ix : iz
  const pMin = alongX ? -iz : -ix
  const pMax = alongX ? iz : ix
  const wellL0 = alongX ? sw.x - sw.w / 2 : sw.z - sw.d / 2
  const wellL1 = alongX ? sw.x + sw.w / 2 : sw.z + sw.d / 2
  const wellP0 = alongX ? sw.z - sw.d / 2 : sw.x - sw.w / 2
  const wellP1 = alongX ? sw.z + sw.d / 2 : sw.x + sw.w / 2
  if (sMax - sMin < 1.5 || pMax - pMin < 1.5) return

  const put = (p: number, a: number, c: number, jw: number, jh: number) => {
    if (c - a < 0.3) return
    if (alongX) b.plate('woodBeam', c - a, jh, jw, (a + c) / 2, soffit - jh / 2, p)
    else b.plate('woodBeam', jw, jh, c - a, p, soffit - jh / 2, (a + c) / 2)
  }

  // Downstand binder across the joist run, on corbels. Held to 0.26 m and hung
  // from the soffit rather than from under the joists: the deepest opening head
  // in the kit sits 0.35 m below a slab, and a binder any lower would cut
  // across a doorway — including the blown-out opening the interior pose is
  // composed through.
  const beams = sMax - sMin > 7.5 ? 2 : 1
  const bd = 0.26
  for (let i = 0; i < beams; i++) {
    const at = sMin + ((i + 1) * (sMax - sMin)) / (beams + 1)
    if (alongX) {
      b.plate('woodBeam', 0.26, bd, pMax - pMin, at, soffit - bd / 2, (pMin + pMax) / 2)
      for (const sp of [pMin, pMax]) b.plate('stoneBlock', 0.4, 0.17, 0.34, at, soffit - 0.15, sp)
    } else {
      b.plate('woodBeam', pMax - pMin, bd, 0.26, (pMin + pMax) / 2, soffit - bd / 2, at)
      for (const sp of [pMin, pMax]) b.plate('stoneBlock', 0.34, 0.17, 0.4, sp, soffit - 0.15, at)
    }
  }

  const n = Math.floor((pMax - pMin - 0.3) / rng.range(0.48, 0.58))
  const spacing = (pMax - pMin - 0.3) / (n + 1)
  for (let i = 0; i <= n; i++) {
    const p = pMin + 0.15 + (i + 0.5) * spacing
    // One joist gone, and the bare soffit showing in the bay where it was, is
    // what stops twenty-odd identical members reading as a comb.
    if (rng.bool(0.07)) continue
    const jh = rng.range(0.16, 0.2)
    const jw = rng.range(0.075, 0.095)
    if (p > wellP0 && p < wellP1) {
      put(p, sMin, wellL0, jw, jh)
      put(p, wellL1, sMax, jw, jh)
    } else {
      put(p, sMin, sMax, jw, jh)
    }
    // Every third bay gets a strut nogged between the joists, staggered, which
    // is both how a floor is stiffened and a second shadow direction up there.
    if (i % 3 === 2 && i < n) {
      const at = sMin + (sMax - sMin) * rng.range(0.25, 0.75)
      if (alongX) b.plate('woodBeam', 0.06, jh * 0.8, spacing * 0.78, at, soffit - jh * 0.5, p + spacing * 0.5)
      else b.plate('woodBeam', spacing * 0.78, jh * 0.8, 0.06, p + spacing * 0.5, soffit - jh * 0.5, at)
    }
  }
}

export interface BuildResult {
  /** Roofed volumes, in world space, for `isIndoors`. */
  indoor: THREE.Box3[]
  /** Flat rooftop decks the player and props can stand on. */
  decks: { cx: number; cz: number; w: number; d: number; y: number; yaw: number }[]
}

/**
 * Assembles a rectangular building from the kit: plinth, four dressed facades
 * per storey, floor slabs, roof slab, parapet with coping, and either an
 * inhabited interior or a light-blocking inner shell.
 */
export function buildBuilding(b: Builder, spec: BuildingSpec, rng: Rand, result: BuildResult): number {
  const w = spec.w
  const d = spec.d
  const yaw = spec.yaw ?? 0
  const sh = spec.storeyH ?? STOREY
  const base = footprintBase(spec.cx, spec.cz, w, d)
  const trim = spec.trim ?? 'stoneBlock'
  const roofMat = spec.roof ?? 'concreteWorn'
  const parapet = spec.parapet ?? PARAPET_H
  const damage = spec.damage ?? 0.5
  const T = WALL_T

  b.push(spec.cx, base, spec.cz, yaw)

  // Foundation plinth — buries the footprint into the undulating ground.
  b.solid(trim, w + 0.22, 1.5, d + 0.22, 0, -0.72, 0, 0, 0.05, 'concrete')

  // Stairwell reserved against the east wall, running north to south.
  const sw = {
    w: 1.55,
    d: Math.min(5.1, d - 2.2),
    x: w / 2 - T - 0.95,
    z: -d / 2 + T + Math.min(5.1, d - 2.2) / 2 + 0.35,
  }

  for (let s = 0; s < spec.storeys; s++) {
    const y0 = s * sh
    for (const side of SIDES) {
      const face = spec.faces[side]
      if (!face) continue
      const p = sidePlacement(side, w, d, T)
      const bay = face.bay ?? 2.9
      const openings = s === 0
        ? groundOpenings(face.ground, p.length, bay, rng)
        : upperOpenings(face.upper, p.length, bay, rng)
      b.push(p.x, y0, p.z, p.yaw)
      buildWall(b, {
        length: p.length,
        height: sh,
        thickness: T,
        mat: spec.wall,
        trim,
        openings,
        exterior: true,
        plinth: s === 0 ? 0.42 : 0,
        cornice: s === spec.storeys - 1,
        weather: damage,
        backOpenings: !spec.enterable,
        dressInside: spec.enterable,
        // A storey carries a balcony or a projecting window box, never both:
        // the balcony is hung over the middle opening and the box picks a
        // window at random, so the two would sooner or later land on the same
        // one and interpenetrate.
        upperFloor: s > 0 && face.upper !== 'balcony',
        rng,
      })
      // Balcony slab on upper storeys that call for one.
      if (s > 0 && face.upper === 'balcony' && openings.length > 0) {
        const o = openings[Math.floor(openings.length / 2)]
        const bw = Math.min(p.length - 0.6, o.width + 2.0)
        b.solid(trim, bw, 0.14, 1.15, o.at, 0.07, -T / 2 - 0.575, 0, 0.03)
        // Corbels carrying the slab: without them it cantilevers out of blank
        // render, which is the single most obvious "extruded box" tell there is.
        for (let i = 0; i < 3; i++) {
          b.slab(trim, 0.13, 0.22, 0.62, o.at - bw / 2 + (bw * (i + 0.5)) / 3, -0.1, -T / 2 - 0.32)
        }
        // Balusters at 11 cm, not 7 bars across two metres. The rail is the
        // only thing on this facade with detail finer than a window, and at
        // 15-25 m it is what stops the upper storey reading as flat.
        const n = Math.max(10, Math.round(bw / 0.11))
        for (let i = 0; i <= n; i++) {
          b.plate('metalRusted', 0.022, 0.95, 0.022, o.at - bw / 2 + (bw * i) / n, 0.62, -T / 2 - 1.1)
        }
        // Returns down both sides of the balcony, same pitch.
        for (const sx of [-1, 1]) {
          for (let i = 1; i < 5; i++) {
            b.plate('metalRusted', 0.022, 0.95, 0.022, o.at + sx * bw / 2, 0.62, -T / 2 - 1.1 + (i * 1.0) / 5)
          }
        }
        b.slab('metalRusted', bw + 0.06, 0.05, 0.07, o.at, 1.115, -T / 2 - 1.1)
        b.plate('metalRusted', bw, 0.035, 0.035, o.at, 0.68, -T / 2 - 1.1)
        b.plate('metalRusted', bw, 0.035, 0.035, o.at, 0.2, -T / 2 - 1.1)
        for (const sx of [-1, 1]) {
          b.slab('metalRusted', 0.05, 0.05, 1.12, o.at + sx * bw / 2, 1.115, -T / 2 - 0.58)
          b.plate('metalRusted', 0.03, 0.95, 1.1, o.at + sx * bw / 2, 0.62, -T / 2 - 0.58)
        }
        // Something always lives on a balcony.
        b.geom('metalRusted', cylinderGeom(0.026, 0.026, bw - 0.2, 5),
          xform(o.at, 1.6, -T / 2 - 0.5, 0, 0, Math.PI / 2))
        b.geom('metalPainted', cylinderGeom(0.14, 0.11, 0.26, 9), xform(o.at + bw * 0.3, 0.24, -T / 2 - 0.85))
        b.geom('dirt', decalQuad(bw * 0.8, 0.5, 0.28, o.at * 7), xform(o.at, -0.28, -T / 2 - 0.05))
      }
      b.pop()
    }
    // Floor slab between storeys, with a stairwell void when enterable.
    if (s > 0) {
      if (spec.enterable) {
        slabWithWell(b, roofMat, w, d, T, y0, sw)
        ceilingJoists(b, rng, w, d, T, y0, sw)
      } else b.solid(roofMat, w - T * 1.6, SLAB_T, d - T * 1.6, 0, y0 - SLAB_T / 2, 0, 0, 0.02, 'concrete')
      buildStairFlight(b, sw.x, (s - 1) * sh, sw.z - sw.d / 2 + 0.18, Math.PI, sw.w - 0.2, 18, sh / 18, 0.26, 'concreteWorn', true)
    }
  }

  // Corner quoins. A building's corner is the edge the eye reads its mass
  // from, and a plain extruded corner is the fastest way to make a facade look
  // like a blockout. Alternating stones give that edge a rhythm, and because
  // they stand 5 cm proud each one throws a shadow across the render below it.
  // Only three buildings in five have them: quoining every corner in the
  // district would be its own kind of procedural regularity.
  if (rng.bool(0.6)) {
    const qh = rng.range(0.52, 0.78)
    const rows = Math.max(2, Math.floor((spec.storeys * sh - 0.5) / qh))
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        for (let i = 0; i < rows; i++) {
          const y = 0.28 + (i + 0.5) * qh
          const long = i % 2 === 0
          const a = long ? rng.range(0.5, 0.68) : rng.range(0.26, 0.36)
          const c = long ? rng.range(0.26, 0.36) : rng.range(0.5, 0.68)
          b.box(trim, a, qh * 0.9, 0.11, sx * (w / 2 - a / 2 + 0.03), y, sz * (d / 2 + 0.02), 0, 0.02)
          b.box(trim, 0.11, qh * 0.9, c, sx * (w / 2 + 0.02), y, sz * (d / 2 - c / 2 + 0.03), 0, 0.02)
        }
      }
    }
  }

  // Interior partitions.
  for (const p of spec.partitions ?? []) {
    const y0 = p.storey * sh
    const len = p.to - p.from
    if (p.axis === 'x') b.push(p.from, y0, p.at, 0)
    else b.push(p.at, y0, p.from, -Math.PI / 2)
    buildWall(b, {
      length: len, height: sh - SLAB_T, thickness: 0.22, mat: p.mat ?? 'plasterWhite',
      trim: 'plasterDamaged', openings: p.openings ?? [], exterior: false, weather: 0, rng,
      surface: 'plaster',
    })
    b.pop()
  }

  const roofY = spec.storeys * sh
  // Roof slab and parapet.
  if (spec.enterable) {
    slabWithWell(b, roofMat, w, d, T, roofY, sw)
    ceilingJoists(b, rng, w, d, T, roofY, sw)
    buildStairFlight(b, sw.x, (spec.storeys - 1) * sh, sw.z - sw.d / 2 + 0.18, Math.PI, sw.w - 0.2, 18, sh / 18, 0.26, 'concreteWorn', true)
    // Bulkhead hut over the stair head, with a doorway onto the deck.
    b.push(sw.x, roofY, sw.z, 0)
    const bw = sw.w + 0.7
    const bd = sw.d * 0.55
    b.solid(spec.wall, bw, 2.35, 0.24, 0, 1.18, -bd / 2, 0, 0.03)
    b.solid(spec.wall, 0.24, 2.35, bd, -bw / 2, 1.18, 0, 0, 0.03)
    b.solid(spec.wall, 0.24, 2.35, bd, bw / 2, 1.18, 0, 0, 0.03)
    b.solid(roofMat, bw + 0.3, 0.18, bd + 0.3, 0, 2.44, 0, 0, 0.03, 'concrete')
    b.pop()
  } else {
    b.solid(roofMat, w - T * 0.2, SLAB_T, d - T * 0.2, 0, roofY - SLAB_T / 2, 0, 0, 0.03, 'concrete')
  }
  if (parapet > 0) {
    const pt = 0.24
    const gaps = damage > 0.6
    for (const side of SIDES) {
      const p = sidePlacement(side, w, d, pt)
      b.push(p.x, roofY, p.z, p.yaw)
      if (gaps && rng.bool(0.4)) {
        // A blown-out section of parapet, with the rest still standing.
        const cut = rng.range(0.25, 0.55)
        const cutAt = rng.range(0.2, 0.8)
        const a = p.length * (cutAt - cut / 2)
        const c = p.length * (cutAt + cut / 2)
        if (a > 0.3) {
          b.solid(spec.wall, a, parapet, pt, a / 2, parapet / 2, 0, 0, 0.03)
          b.push(0, 0, 0, 0)
          copingRun(b, trim, a, parapet, pt, rng)
          b.pop()
        }
        if (c < p.length - 0.3) {
          const rh = parapet * rng.range(0.5, 0.9)
          b.solid(spec.wall, p.length - c, rh, pt, (p.length + c) / 2, rh / 2, 0, 0, 0.03)
          b.push(c, 0, 0, 0)
          copingRun(b, trim, p.length - c, rh, pt, rng)
          b.pop()
        }
        b.solid(spec.wall, c - a, parapet * rng.range(0.2, 0.45), pt, (a + c) / 2, parapet * 0.16, 0, 0, 0.03)
      } else {
        b.solid(spec.wall, p.length, parapet, pt, p.length / 2, parapet / 2, 0, 0, 0.03)
        copingRun(b, trim, p.length, parapet, pt, rng)
      }
      parapetClutter(b, p.length, parapet, pt, rng)
      b.pop()
    }
    result.decks.push({ cx: spec.cx, cz: spec.cz, w: w - 1.2, d: d - 1.2, y: base + roofY, yaw })
  }

  // Drainpipe down one corner, with brackets.
  {
    const cx = (rng.bool() ? 1 : -1) * (w / 2 - 0.22)
    const cz = (rng.bool() ? 1 : -1) * (d / 2 + 0.1)
    const total = roofY
    b.geom('metalRusted', cylinderGeom(0.055, 0.055, total, 8), xform(cx, total / 2, cz))
    for (let i = 0; i < Math.floor(total / 1.4); i++) {
      b.plate('metalRusted', 0.14, 0.05, 0.16, cx, 0.7 + i * 1.4, cz - Math.sign(cz) * 0.08)
    }
    b.geom('metalRusted', cylinderGeom(0.075, 0.075, 0.3, 8), xform(cx, total + 0.1, cz))
    // Splash stain where it discharges.
    b.geom('dirt', decalQuad(0.8, 0.9, 0.25, 17), xform(cx, 0.45, cz - Math.sign(cz) * 0.06))
  }

  // Surface-run conduit, a junction box and a meter — the clutter that makes a
  // wall look inhabited rather than extruded.
  {
    const p = sidePlacement(rng.pick(SIDES), w, d, T)
    b.push(p.x, 0, p.z, p.yaw)
    const runX = rng.range(1.0, Math.max(1.2, p.length - 1.0))
    const hgt = rng.range(2.1, 3.0)
    b.geom('metalPainted', cylinderGeom(0.028, 0.028, hgt, 6), xform(runX, hgt / 2, -T / 2 - 0.045))
    b.geom('metalPainted', cylinderGeom(0.028, 0.028, Math.min(2.2, p.length - runX - 0.4), 6),
      xform(runX + Math.min(2.2, p.length - runX - 0.4) / 2, hgt, -T / 2 - 0.045, 0, 0, Math.PI / 2))
    b.plate('metalPainted', 0.24, 0.3, 0.13, runX, 1.55, -T / 2 - 0.075)
    b.pop()
  }

  if (!spec.enterable) {
    // Light-tight inner shell: peering through a window shows a dark room, and
    // the sun cannot leak through the building.
    // Asphalt rather than concrete: this shell is what every unglazed window
    // on the building looks onto, and a pale grey box behind an opening reads
    // as smoke rather than as an unlit room.
    b.box('asphalt', Math.max(0.4, w - 3.0), roofY - 0.4, Math.max(0.4, d - 3.0), 0, roofY / 2 - 0.2, 0, 0, 0.05)
    if (spec.interiorFloor !== false) {
      b.solid(roofMat, w - T * 1.6, 0.3, d - T * 1.6, 0, -0.15, 0, 0, 0.02, 'concrete')
    }
  } else {
    b.solid('tileFloor', w - T * 1.6, 0.3, d - T * 1.6, 0, -0.15, 0, 0, 0.02, 'tile')
    const box = new THREE.Box3()
    const half = new THREE.Vector3(w / 2, 0, d / 2)
    const c = new THREE.Vector3(spec.cx, base + roofY / 2, spec.cz)
    box.setFromCenterAndSize(c, new THREE.Vector3(Math.abs(half.x * 2) - 0.6, roofY, Math.abs(half.z * 2) - 0.6))
    result.indoor.push(box)
  }

  b.pop()
  return base
}

// ---------------------------------------------------------------------------
// The district
// ---------------------------------------------------------------------------

const F = (ground: GroundStyle, upper: UpperStyle, bay?: number): FacadeSpec => ({ ground, upper, bay })

/**
 * Hand-composed layout. Two flanking routes (a wide market street west, a
 * narrow alley east) run south from a raised market square; a demolished block
 * and the road out of town close the south-east.
 */
export const BUILDINGS: BuildingSpec[] = [
  // --- Interior hero: the bakery on the plaza's south side -----------------
  {
    id: 'bakery', cx: -16.5, cz: 5.75, w: 14, d: 12.5, storeys: 2,
    wall: 'plasterOchre', trim: 'stoneBlock', damage: 0.45, enterable: true,
    faces: {
      n: F('shopdoor', 'balcony', 3.0),
      e: F('windows', 'windows', 2.7),
      s: F('door', 'windows', 3.0),
      w: F('windows', 'windows', 3.2),
    },
    // The cross wall the interior pose looks through: a blown-out opening at
    // eye height frames the sunlit far room.
    partitions: [
      {
        axis: 'z', at: 0, from: -6.25, to: 6.25, storey: 0, mat: 'plasterDamaged',
        openings: [
          { at: 8.5, width: 2.35, sill: 0, height: 2.55, kind: 'hole' },
          { at: 2.6, width: 0.95, sill: 0, height: DOOR_H, kind: 'door' },
        ],
      },
      { axis: 'x', at: -1.6, from: -6.9, to: 0, storey: 0, mat: 'plasterWhite', openings: [{ at: 3.4, width: 1.0, sill: 0, height: DOOR_H, kind: 'door' }] },
      { axis: 'z', at: 0.6, from: -6.25, to: 6.25, storey: 1, mat: 'plasterWhite', openings: [{ at: 7.4, width: 1.9, sill: 0, height: 2.4, kind: 'hole' }] },
    ],
  },
  // --- Market street, west side -------------------------------------------
  {
    id: 'apartment', cx: -15.75, cz: 25, w: 10.5, d: 14, storeys: 2,
    wall: 'plasterWhite', trim: 'concreteWorn', damage: 0.7, enterable: true,
    faces: {
      n: F('windows', 'windows', 3.0),
      e: F('shopdoor', 'balcony', 3.1),
      s: F('windows', 'windows', 3.0),
      w: F('blank', 'windows', 3.4),
    },
    partitions: [
      { axis: 'x', at: 0.5, from: -5.25, to: 1.2, storey: 0, openings: [{ at: 3.6, width: 1.0, sill: 0, height: DOOR_H, kind: 'door' }] },
      { axis: 'x', at: 1.5, from: -5.25, to: 1.0, storey: 1, openings: [{ at: 3.2, width: 1.0, sill: 0, height: DOOR_H, kind: 'door' }] },
    ],
  },
  {
    id: 'westrow', cx: -27.5, cz: 24, w: 10, d: 20, storeys: 2,
    wall: 'stuccoTan', trim: 'stoneBlock', damage: 0.6,
    faces: { n: F('windows', 'windows'), e: F('windows', 'windows', 3.4), s: F('door', 'windows'), w: F('blank', 'blank') },
  },
  {
    id: 'streetEnd', cx: -11, cz: 40.5, w: 15, d: 11, storeys: 2,
    wall: 'brickPainted', trim: 'concreteWorn', damage: 0.8,
    faces: { n: F('shopdoor', 'balcony', 3.2), e: F('windows', 'windows'), s: F('blank', 'windows'), w: F('blank', 'blank') },
  },
  // --- Between the two routes ---------------------------------------------
  {
    id: 'cornerShop', cx: 3.25, cz: 15.75, w: 3.5, d: 5.5, storeys: 1, storeyH: 3.4,
    wall: 'plasterDamaged', trim: 'concreteWorn', damage: 0.85, parapet: 0.95,
    faces: { n: F('door', 'blank'), e: F('windows', 'blank', 2.4), s: F('blank', 'blank'), w: F('shop', 'blank') },
  },
  {
    id: 'wedge', cx: 2.25, cz: 29.5, w: 5.5, d: 11, storeys: 2,
    wall: 'plasterOchre', trim: 'stoneBlock', damage: 0.7,
    faces: { n: F('windows', 'windows', 2.4), e: F('shopdoor', 'windows', 3.0), s: F('door', 'windows'), w: F('shop', 'balcony', 3.0) },
  },
  // --- Alley east side ------------------------------------------------------
  {
    id: 'eastblock', cx: 12.35, cz: 13.25, w: 6.3, d: 12.5, storeys: 2,
    wall: 'brickPainted', trim: 'concreteWorn', damage: 0.75, enterable: true,
    faces: { n: F('windows', 'windows', 2.8), e: F('door', 'windows', 3.0), s: F('windows', 'balcony'), w: F('shopdoor', 'loggia', 2.9) },
    partitions: [
      { axis: 'x', at: 2.6, from: -3.15, to: 3.15, storey: 0, mat: 'plasterDamaged', openings: [{ at: 2.2, width: 1.7, sill: 0, height: 2.4, kind: 'hole' }] },
      { axis: 'x', at: 3.0, from: -3.15, to: 3.15, storey: 1, openings: [{ at: 3.6, width: 1.0, sill: 0, height: DOOR_H, kind: 'door' }] },
    ],
  },
  // --- Plaza east row -------------------------------------------------------
  {
    id: 'plazaE1', cx: 10, cz: -8.5, w: 10, d: 9, storeys: 2,
    wall: 'stuccoTan', trim: 'stoneBlock', damage: 0.4,
    faces: { n: F('windows', 'windows'), e: F('blank', 'windows'), s: F('windows', 'balcony'), w: F('shopdoor', 'balcony', 3.0) },
  },
  {
    id: 'plazaE2', cx: 10, cz: -18.5, w: 10, d: 10, storeys: 3,
    wall: 'plasterWhite', trim: 'stoneBlock', damage: 0.35,
    faces: { n: F('windows', 'windows'), e: F('blank', 'windows'), s: F('windows', 'windows'), w: F('shop', 'balcony', 2.8) },
  },
  {
    id: 'plazaE3', cx: 11, cz: -28, w: 12, d: 8, storeys: 2,
    wall: 'brickRed', trim: 'concreteWorn', damage: 0.55,
    faces: { n: F('windows', 'windows'), e: F('blank', 'blank'), s: F('arch', 'windows', 3.2), w: F('windows', 'windows') },
  },
  // --- Plaza west row -------------------------------------------------------
  {
    id: 'plazaW1', cx: -29.5, cz: -8, w: 9, d: 12, storeys: 2,
    wall: 'plasterOchre', trim: 'stoneBlock', damage: 0.5,
    faces: { n: F('windows', 'windows'), e: F('shopdoor', 'balcony', 3.0), s: F('windows', 'windows'), w: F('blank', 'blank') },
  },
  {
    id: 'plazaW2', cx: -30.5, cz: -20, w: 11, d: 12, storeys: 3,
    wall: 'stuccoTan', trim: 'stoneBlock', damage: 0.45,
    faces: { n: F('windows', 'windows'), e: F('arch', 'loggia', 3.1), s: F('windows', 'windows'), w: F('blank', 'blank') },
  },
  // --- North of the plaza ---------------------------------------------------
  {
    id: 'civic', cx: 0, cz: -34.5, w: 16, d: 10, storeys: 2, storeyH: 3.6,
    wall: 'stoneBlock', trim: 'concreteWorn', damage: 0.3,
    faces: { n: F('blank', 'blank'), e: F('windows', 'windows'), s: F('arch', 'loggia', 3.4), w: F('windows', 'windows') },
  },
  {
    id: 'northrow', cx: -30, cz: -46, w: 20, d: 10, storeys: 2,
    wall: 'plasterWhite', trim: 'stoneBlock', damage: 0.4,
    faces: { n: F('blank', 'blank'), e: F('windows', 'windows'), s: F('windows', 'windows'), w: F('blank', 'blank') },
  },
  // --- East of the plaza, across the road ----------------------------------
  {
    id: 'eastrow1', cx: 30, cz: -12, w: 14, d: 16, storeys: 3,
    wall: 'plasterOchre', trim: 'stoneBlock', damage: 0.4,
    faces: { n: F('windows', 'windows'), e: F('blank', 'blank'), s: F('windows', 'windows'), w: F('shopdoor', 'balcony', 3.2) },
  },
  {
    id: 'eastrow2', cx: 31, cz: 6, w: 16, d: 14, storeys: 2,
    wall: 'stuccoTan', trim: 'concreteWorn', damage: 0.5,
    faces: { n: F('windows', 'windows'), e: F('blank', 'blank'), s: F('windows', 'windows'), w: F('windows', 'windows') },
  },
  // --- Compound north of the highway ---------------------------------------
  {
    id: 'compound', cx: 30, cz: 18, w: 12, d: 9, storeys: 2,
    wall: 'plasterDamaged', trim: 'concreteWorn', damage: 0.8,
    faces: { n: F('windows', 'windows'), e: F('windows', 'windows'), s: F('shopdoor', 'balcony', 3.0), w: F('ruin', 'windows') },
  },
  // --- South of the highway -------------------------------------------------
  {
    id: 'southrow', cx: 24, cz: 44, w: 20, d: 10, storeys: 2,
    wall: 'brickPainted', trim: 'concreteWorn', damage: 0.7,
    faces: { n: F('shopdoor', 'windows', 3.4), e: F('windows', 'windows'), s: F('blank', 'blank'), w: F('windows', 'windows') },
  },
  {
    id: 'southwest', cx: -2, cz: 46, w: 14, d: 10, storeys: 2,
    wall: 'stuccoTan', trim: 'stoneBlock', damage: 0.6,
    faces: { n: F('windows', 'windows'), e: F('windows', 'windows'), s: F('blank', 'blank'), w: F('blank', 'blank') },
  },
  {
    id: 'westfar', cx: -34, cz: 8, w: 12, d: 14, storeys: 3,
    wall: 'plasterWhite', trim: 'stoneBlock', damage: 0.45,
    faces: { n: F('windows', 'windows'), e: F('windows', 'balcony', 3.2), s: F('windows', 'windows'), w: F('blank', 'blank') },
  },
  {
    id: 'westfar2', cx: -36, cz: 34, w: 14, d: 16, storeys: 2,
    wall: 'stuccoTan', trim: 'concreteWorn', damage: 0.6,
    faces: { n: F('windows', 'windows'), e: F('windows', 'windows'), s: F('blank', 'blank'), w: F('blank', 'blank') },
  },
]

/** Footprints used to keep scatter systems from placing props inside walls. */
export function insideAnyBuilding(x: number, z: number, margin = 0.6): boolean {
  for (const s of BUILDINGS) {
    if (rotRectSdf(x, z, s.cx, s.cz, s.w / 2 + margin, s.d / 2 + margin, s.yaw ?? 0) < 0) return true
  }
  for (const s of EXTRA_FOOTPRINTS) {
    if (rotRectSdf(x, z, s.cx, s.cz, s.hw + margin, s.hd + margin, s.yaw) < 0) return true
  }
  return false
}

/** Custom structures that are not generic rectangles but still block placement. */
export const EXTRA_FOOTPRINTS: { cx: number; cz: number; hw: number; hd: number; yaw: number }[] = [
  { cx: -21.5, cz: -36, hw: 8.5, hd: 6, yaw: 0 }, // mosque
  { cx: -15, cz: -30.5, hw: 1.5, hd: 1.5, yaw: 0 }, // minaret
  { cx: 19.75, cz: 16.25, hw: 4.25, hd: 5.75, yaw: 0 }, // market hall
  { cx: 9.35, cz: 29.5, hw: 0.35, hd: 5.8, yaw: 0 }, // alley compound wall
  { cx: 31, cz: 27.5, hw: 2.2, hd: 2.2, yaw: 0 }, // water tower legs
  { cx: 7.15, cz: 34.7, hw: 2.4, hd: 1.5, yaw: 0 }, // alley gateway — keep the passage clear
]

// ---------------------------------------------------------------------------
// Landmarks
// ---------------------------------------------------------------------------

/** The mosque closing the plaza's north side: iwan arch, dome, stone banding. */
export function buildMosque(b: Builder, rng: Rand, result: BuildResult): void {
  const cx = -21.5
  const cz = -36
  const w = 17
  const d = 12
  const h = 8.6
  const base = footprintBase(cx, cz, w, d)
  b.push(cx, base, cz, 0)
  b.solid('stoneBlock', w + 0.3, 1.6, d + 0.3, 0, -0.78, 0, 0, 0.06, 'concrete')

  const T = 0.45
  // South facade with a monumental iwan.
  b.push(w / 2, 0, d / 2 - T / 2, Math.PI)
  buildWall(b, {
    length: w, height: h, thickness: T, mat: 'stuccoTan', trim: 'stoneBlock',
    exterior: true, plinth: 0.5, cornice: true, weather: 0.3, rng, backOpenings: true,
    openings: [
      { at: w / 2, width: 4.6, sill: 0, height: 6.6, kind: 'arch' },
      { at: 2.4, width: 1.1, sill: 4.0, height: 1.9, kind: 'arch', glass: false },
      { at: w - 2.4, width: 1.1, sill: 4.0, height: 1.9, kind: 'arch', glass: false },
    ],
  })
  b.pop()
  for (const [side, sx, sz, syaw, len] of [
    ['n', -w / 2, -d / 2 + T / 2, 0, w],
    ['e', w / 2 - T / 2, -d / 2, -Math.PI / 2, d],
    ['w', -w / 2 + T / 2, d / 2, Math.PI / 2, d],
  ] as [string, number, number, number, number][]) {
    void side
    b.push(sx, 0, sz, syaw)
    buildWall(b, {
      length: len, height: h, thickness: T, mat: 'stuccoTan', trim: 'stoneBlock',
      exterior: true, plinth: 0.5, cornice: true, weather: 0.3, rng, backOpenings: true,
      openings: bayPositions(len, 3.6, 1.6).map((at) => ({
        at, width: 1.0, sill: 4.4, height: 2.0, kind: 'arch' as const, glass: false,
      })),
    })
    b.pop()
  }
  // Interior shell so the iwan reads as a deep, dark recess.
  b.box('asphalt', w - 3.6, h - 0.6, d - 3.6, 0, (h - 0.6) / 2, 0, 0, 0.05)
  b.solid('concreteWorn', w - 0.4, SLAB_T, d - 0.4, 0, h + SLAB_T / 2, 0, 0, 0.04, 'concrete')

  // Drum and dome.
  const drumY = h + SLAB_T
  b.geom('stuccoTan', cylinderGeom(4.3, 4.6, 1.8, 20), xform(0, drumY + 0.9, 0))
  b.geom('stoneBlock', cylinderGeom(4.7, 4.7, 0.22, 20), xform(0, drumY + 1.9, 0))
  b.geom('plasterWhite', sphereGeom(4.35, 22, 12, Math.PI * 2, Math.PI / 2), xform(0, drumY + 1.9, 0))
  b.geom('metalPainted', cylinderGeom(0.09, 0.16, 1.5, 8), xform(0, drumY + 6.9, 0))
  b.geom('metalPainted', sphereGeom(0.28, 10, 8), xform(0, drumY + 7.7, 0))
  b.collide(8.6, 5.5, 8.6, 0, drumY + 2.7, 0, 0, 'concrete')

  // Corner buttresses.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      b.solid('stoneBlock', 1.3, h + 1.2, 1.3, sx * (w / 2 - 0.3), (h + 1.2) / 2, sz * (d / 2 - 0.3), 0, 0.06)
      b.geom('stoneBlock', sphereGeom(0.5, 10, 6), xform(sx * (w / 2 - 0.3), h + 1.2, sz * (d / 2 - 0.3)))
    }
  }
  b.pop()
  result.indoor.push(new THREE.Box3().setFromCenterAndSize(
    new THREE.Vector3(cx, base + h / 2, cz), new THREE.Vector3(w - 1.5, h, d - 1.5)))
}

/** Minaret — the district's skyline anchor and the plaza pose's back subject. */
export function buildMinaret(b: Builder, rng: Rand): void {
  const cx = -15
  const cz = -30.5
  const base = footprintBase(cx, cz, 3, 3)
  b.push(cx, base, cz, 0.18)
  b.solid('stoneBlock', 3.4, 1.4, 3.4, 0, -0.6, 0, 0, 0.06, 'concrete')
  b.solid('stoneBlock', 3.0, 1.2, 3.0, 0, 0.6, 0, 0, 0.05)
  b.solid('stuccoTan', 2.5, 11.5, 2.5, 0, 1.2 + 5.75, 0, 0, 0.05)
  // Banding and blind arcading up the shaft. The niches are recessed panels
  // inside a chamfered surround rather than flat plates stuck on the render —
  // the minaret is the plaza pose's back subject and its shaft was 11 m of
  // untouched stucco.
  for (let i = 0; i < 5; i++) {
    b.box('stoneBlock', 2.66, 0.16, 2.66, 0, 2.6 + i * 2.2, 0, 0, 0.03)
    b.box('stoneBlock', 2.6, 0.07, 2.6, 0, 2.44 + i * 2.2, 0, 0, 0.02)
    for (let f = 0; f < 4; f++) {
      const yaw = (f * Math.PI) / 2
      b.geom('plasterDamaged', chamferBox(0.6, 1.1, 0.06, 0.016), xform(0, 3.4 + i * 2.2, 1.28, yaw))
      // Surround: two jambs and a head, standing 4 cm proud of the panel.
      for (const sx of [-1, 1]) {
        b.geom('stuccoTan', chamferBox(0.11, 1.24, 0.09, 0.018), xform(sx * 0.355, 3.4 + i * 2.2, 1.29, yaw))
      }
      b.geom('stuccoTan', chamferBox(0.82, 0.11, 0.09, 0.018), xform(0, 4.02 + i * 2.2, 1.29, yaw))
      b.geom('stuccoTan', chamferBox(0.82, 0.09, 0.09, 0.018), xform(0, 2.79 + i * 2.2, 1.29, yaw))
    }
  }
  // Muezzin's gallery.
  const gy = 12.7
  b.solid('stoneBlock', 4.2, 0.28, 4.2, 0, gy, 0, 0, 0.04)
  for (let f = 0; f < 4; f++) {
    b.push(0, 0, 0, (f * Math.PI) / 2)
    for (let i = 0; i < 7; i++) {
      b.plate('stoneBlock', 0.11, 0.85, 0.11, -1.8 + i * 0.6, gy + 0.57, 2.0)
    }
    b.box('stoneBlock', 4.2, 0.12, 0.18, 0, gy + 1.05, 2.0, 0, 0.03)
    b.pop()
  }
  b.solid('stuccoTan', 2.0, 5.4, 2.0, 0, gy + 0.28 + 2.7, 0, 0, 0.04)
  for (let f = 0; f < 4; f++) {
    b.plate('plasterDamaged', 0.5, 1.5, 0.06, 0, gy + 2.2, 1.02, (f * Math.PI) / 2)
  }
  b.geom('stoneBlock', cylinderGeom(1.35, 1.5, 0.3, 12), xform(0, gy + 5.85, 0))
  b.geom('plasterWhite', sphereGeom(1.35, 14, 9, Math.PI * 2, Math.PI / 2), xform(0, gy + 6.0, 0))
  b.geom('metalPainted', cylinderGeom(0.05, 0.09, 1.3, 8), xform(0, gy + 8.0, 0))
  b.geom('metalPainted', sphereGeom(0.2, 8, 6), xform(0, gy + 8.7, 0))
  // Loudspeakers on the gallery, pointing over the square.
  for (let f = 0; f < 4; f++) {
    const a = (f * Math.PI) / 2 + 0.4
    b.geom('metalPainted', cylinderGeom(0.26, 0.1, 0.42, 10),
      xform(Math.sin(a) * 1.1, gy + 1.6, Math.cos(a) * 1.1, a, Math.PI / 2))
  }
  void rng
  b.pop()
}

/**
 * The covered market hall east of the alley. Its 2.8 m roof deck is the
 * district's elevated overwatch position and the vista pose's vantage point.
 */
export function buildMarketHall(b: Builder, rng: Rand, result: BuildResult): void {
  const cx = 19.75
  const cz = 16.25
  const w = 8.5
  const d = 11.5
  const deck = 2.8
  const base = footprintBase(cx, cz, w, d)
  const T = 0.3
  b.push(cx, base, cz, 0)
  b.solid('stoneBlock', w + 0.24, 1.4, d + 0.24, 0, -0.68, 0, 0, 0.05, 'concrete')

  const faces: [Side, Opening[]][] = [
    ['n', [{ at: w / 2, width: 3.2, sill: 0, height: 2.35, kind: 'arch' }]],
    ['e', [
      { at: 3.0, width: 1.1, sill: 1.15, height: 1.1, kind: 'window', bars: true },
      { at: 8.4, width: 1.1, sill: 1.15, height: 1.1, kind: 'window', bars: true },
    ]],
    ['s', [{ at: w / 2, width: 2.4, sill: 0, height: 2.3, kind: 'arch' }]],
    // Local x runs from z = +d/2 back to -d/2; keep the openings clear of the
    // exterior stair that lands at the north end of this face.
    ['w', [
      { at: 5.6, width: 2.6, sill: 0.35, height: 2.0, kind: 'shop', glass: false },
      { at: 9.2, width: DOOR_W, sill: 0, height: DOOR_H, kind: 'door' },
    ]],
  ]
  for (const [side, openings] of faces) {
    const p = sidePlacement(side, w, d, T)
    b.push(p.x, 0, p.z, p.yaw)
    buildWall(b, {
      length: p.length, height: deck, thickness: T, mat: 'brickPainted', trim: 'concreteWorn',
      openings, exterior: true, plinth: 0.35, weather: 0.7, rng,
    })
    b.pop()
  }
  // Deck slab, then a low parapet the player can shoot over from a crouch.
  b.solid('concreteWorn', w, 0.26, d, 0, deck - 0.13, 0, 0, 0.03, 'concrete')
  const pt = 0.22
  const parapet = 0.84
  for (const side of SIDES) {
    const p = sidePlacement(side, w, d, pt)
    b.push(p.x, deck, p.z, p.yaw)
    if (side === 'w') {
      // Open where the exterior stair lands — the way onto the deck.
      const gap = 4.2
      b.solid('brickPainted', p.length - gap, parapet, pt, gap + (p.length - gap) / 2, parapet / 2, 0, 0, 0.03)
      b.push(gap, 0, 0, 0)
      copingRun(b, 'concreteWorn', p.length - gap, parapet, pt, rng)
      b.pop()
    } else if (side === 's') {
      // A shell took out the middle of this run. The breach is what makes the
      // overwatch position read: the district opens up through it, and the
      // stumps either side frame the shot instead of walling it off.
      const a = 1.8
      const c = 5.0
      b.solid('brickPainted', a, parapet, pt, a / 2, parapet / 2, 0, 0, 0.03)
      copingRun(b, 'concreteWorn', a, parapet, pt, rng)
      b.solid('brickPainted', p.length - c, parapet, pt, (p.length + c) / 2, parapet / 2, 0, 0, 0.03)
      b.push(c, 0, 0, 0)
      copingRun(b, 'concreteWorn', p.length - c, parapet, pt, rng)
      b.pop()
      b.solid('brickPainted', c - a, 0.24, pt, (a + c) / 2, 0.12, 0, 0, 0.03)
      for (let i = 0; i < 5; i++) {
        b.geom('rebar', cylinderGeom(0.013, 0.013, rng.range(0.3, 0.6), 4),
          xform(a + rng.range(0.1, c - a - 0.1), 0.4, rng.spread(0.06), rng.range(0, 3.1), rng.spread(0.6), rng.spread(0.6)))
      }
    } else {
      b.solid('brickPainted', p.length, parapet, pt, p.length / 2, parapet / 2, 0, 0, 0.03)
      copingRun(b, 'concreteWorn', p.length, parapet, pt, rng)
      // Only the two closed runs carry clutter: the west run is the way onto
      // the deck and the south run is the firing line the pose is composed on.
      parapetClutter(b, p.length, parapet, pt, rng)
    }
    b.pop()
  }
  // Interior: an open hall with a beam grid and hanging bulbs.
  b.solid('tileFloor', w - 0.9, 0.24, d - 0.9, 0, -0.1, 0, 0, 0.02, 'tile')
  for (let i = 0; i < 5; i++) {
    b.box('woodBeam', w - 0.6, 0.2, 0.16, 0, deck - 0.42, -d / 2 + 1.4 + i * 2.1, 0, 0.02)
  }
  for (const sx of [-1, 1]) {
    b.solid('woodBeam', 0.22, deck - 0.5, 0.22, sx * 2.4, (deck - 0.5) / 2, 0, 0, 0.025)
  }
  b.pop()

  // Exterior stair climbing north along the west face, from the alley
  // junction up to the deck — the route to the district's overwatch position.
  const stx = cx - w / 2 - 0.85
  const stz = cz + d / 2 + 1.2
  buildStairFlight(b, stx, base, stz, 0, 1.5, 16, deck / 16, 0.26, 'concreteWorn', false)
  b.push(stx, base, stz, 0)
  // Outboard stringer wall, so the flight is not a floating staircase.
  for (let i = 0; i < 16; i++) {
    const top = (i + 1) * (deck / 16)
    b.box('brickPainted', 0.26, top + 0.5, 0.26, -0.88, (top + 0.5) / 2 - 0.25, -(i + 0.5) * 0.26, 0, 0.03)
  }
  // Landing bridging the parapet gap onto the deck.
  b.solid('concreteWorn', 2.4, 0.24, 1.5, 0.55, deck - 0.12, -4.16 - 0.75, 0, 0.03, 'concrete')
  b.pop()

  result.decks.push({ cx, cz, w: w - 1.4, d: d - 1.4, y: base + deck, yaw: 0 })
  result.indoor.push(new THREE.Box3().setFromCenterAndSize(
    new THREE.Vector3(cx, base + deck / 2, cz), new THREE.Vector3(w - 1.0, deck, d - 1.0)))
}

/**
 * An enclosed first-floor passage bridging the alley — the sabat that gives
 * the alley pose a dark ceiling and turns it into a real corridor.
 */
export function buildSabat(b: Builder, rng: Rand, result: BuildResult): void {
  const x0 = 5.0
  const x1 = 9.2
  const zc = 18.0
  const width = 3.0
  const y = footprintBase((x0 + x1) / 2, zc, x1 - x0, width) + STOREY
  const span = x1 - x0
  b.push((x0 + x1) / 2, y, zc, 0)
  b.solid('concreteWorn', span + 0.4, 0.3, width, 0, -0.15, 0, 0, 0.03, 'concrete')
  b.solid('plasterOchre', span + 0.4, 2.55, 0.32, 0, 1.4, -width / 2 + 0.16, 0, 0.03)
  b.solid('plasterOchre', span + 0.4, 2.55, 0.32, 0, 1.4, width / 2 - 0.16, 0, 0.03)
  b.solid('concreteWorn', span + 0.5, 0.26, width + 0.24, 0, 2.8, 0, 0, 0.04, 'concrete')
  // A small barred window over the alley on each side.
  for (const sz of [-1, 1]) {
    b.plate('plasterDamaged', 0.9, 0.9, 0.06, 0.8 * sz, 1.5, sz * (width / 2 - 0.02))
    for (let i = 0; i < 3; i++) {
      b.geom('rebar', cylinderGeom(0.016, 0.016, 0.86, 6), xform(0.8 * sz - 0.3 + i * 0.3, 1.5, sz * (width / 2 - 0.05)))
    }
  }
  // Timber joists poking out under the bridge — a classic medina detail.
  for (let i = 0; i < 5; i++) {
    b.geom('woodBeam', cylinderGeom(0.07, 0.075, span + 0.7, 7),
      xform(0, -0.34, -width / 2 + 0.4 + i * ((width - 0.8) / 4), 0, 0, Math.PI / 2))
  }
  b.geom('dirt', decalQuad(span, 1.2, 0.2, 9), xform(0, 1.0, -width / 2 - 0.02))
  b.pop()
  void rng
  result.indoor.push(new THREE.Box3().setFromCenterAndSize(
    new THREE.Vector3((x0 + x1) / 2, y + 1.3, zc), new THREE.Vector3(span, 2.6, width - 0.4)))
}

/**
 * Boundary walls: they define the alley's southern half and the compounds
 * without costing a full building, and give hard cover along the sightlines.
 */
export function buildCompoundWalls(b: Builder, rng: Rand): void {
  const runs: { x0: number; z0: number; x1: number; z1: number; h: number; mat: MaterialName; gate?: number }[] = [
    // East side of the alley, south of the junction — low enough that sun
    // spills over it onto the alley's west wall.
    { x0: 9.35, z0: 24.0, x1: 9.35, z1: 35.2, h: 2.55, mat: 'brickPainted', gate: 0.62 },
    // Lot boundary along the highway shoulder.
    { x0: 9.6, z0: 35.2, x1: 26.0, z1: 35.2, h: 2.2, mat: 'concreteWorn' },
    { x0: 26.0, z0: 21.5, x1: 26.0, z1: 35.2, h: 1.85, mat: 'stuccoTan' },
    // Compound around the house north of the highway.
    { x0: 22.5, z0: 12.0, x1: 22.5, z1: 24.0, h: 2.7, mat: 'stuccoTan', gate: 0.5 },
    { x0: 22.5, z0: 24.0, x1: 37.5, z1: 24.0, h: 2.7, mat: 'stuccoTan' },
    // Yard behind the apartment block.
    { x0: -21.5, z0: 18.0, x1: -21.5, z1: 33.0, h: 2.4, mat: 'plasterDamaged' },
    { x0: -21.5, z0: 33.0, x1: -10.0, z1: 33.0, h: 2.4, mat: 'plasterDamaged', gate: 0.4 },
    // Plaza's north-west corner.
    { x0: -25.5, z0: -26.5, x1: -25.5, z1: -14.0, h: 2.6, mat: 'stoneBlock' },
  ]
  for (const r of runs) {
    const dx = r.x1 - r.x0
    const dz = r.z1 - r.z0
    const len = Math.hypot(dx, dz)
    const yaw = Math.atan2(-dz, dx)
    const cxm = (r.x0 + r.x1) / 2
    const czm = (r.z0 + r.z1) / 2
    const base = footprintBase(cxm, czm, Math.abs(dx) + 0.4, Math.abs(dz) + 0.4)
    // Pushed at the run's start, not its midpoint: `buildWall` lays its panel
    // from local x = 0 to x = length, which is why every other caller offsets by
    // half the side before pushing. Pushing at the midpoint put the wall half
    // its own length up the run while the coping and the piers below stayed on
    // the declared line, so each boundary had piers carrying nothing at one end
    // and a panel with no coping at the other.
    b.push(r.x0, base, r.z0, yaw)
    const openings: Opening[] = []
    if (r.gate !== undefined) {
      openings.push({ at: len * r.gate, width: 2.4, sill: 0, height: 2.15, kind: 'hole' })
    }
    buildWall(b, {
      length: len, height: r.h, thickness: 0.28, mat: r.mat, trim: 'concreteWorn',
      openings, exterior: true, plinth: 0.3, weather: 0.8, rng, plain: true,
    })
    // Coping laid as stones, and pier caps.
    copingRun(b, 'concreteWorn', len, r.h, 0.28, rng)
    const piers = Math.max(2, Math.round(len / 4))
    for (let i = 0; i <= piers; i++) {
      const px = (len * i) / piers
      b.solid(r.mat, 0.46, r.h + 0.3, 0.46, px, (r.h + 0.3) / 2, 0, 0, 0.035)
      b.box('concreteWorn', 0.58, 0.12, 0.58, px, r.h + 0.36, 0, 0, 0.03)
      // Something is always leaning against a compound pier. A board propped at
      // 15 degrees is the one line on a boundary wall that is neither vertical
      // nor horizontal, and it breaks the pier rhythm the eye is counting.
      if (rng.bool(0.4)) {
        const lean = rng.range(0.13, 0.26) * (rng.bool() ? 1 : -1)
        const lh = rng.range(1.1, 1.9)
        b.geom('woodPainted', chamferBox(rng.range(0.16, 0.32), lh, 0.035, 0.006),
          xform(px + rng.spread(0.6) + Math.sin(lean) * lh / 2, Math.cos(lean) * lh / 2,
            -0.16 - Math.sin(Math.abs(lean)) * lh / 2, 0, 0, -lean))
      }
    }
    b.pop()
  }
}

/**
 * A vaulted gateway closing the far end of the alley.
 *
 * The alley pose's leading lines used to run out into open sand and a blown-out
 * sky: the strongest composition on the map resolved on nothing. This puts a
 * 3 m deep masonry reveal on the vanishing point — dark inside, sunlit beyond —
 * so the corridor terminates on a readable shape with a bright slot behind it
 * for the parked truck to silhouette against. The route stays open.
 */
export function buildAlleyTerminus(b: Builder, rng: Rand, result: BuildResult): void {
  const xL = 4.75
  const xR = 9.55
  const zc = 34.7
  const depth = 3.0
  const span = xR - xL
  const cx = (xL + xR) / 2
  const base = footprintBase(cx, zc, span, depth)
  const H = 6.6
  // Arch centred on the alley pose's sightline rather than on the gateway, so
  // the piers are deliberately unequal and the mass reads as built, not placed.
  const ax = 6.55
  const r = 1.5
  const ys = 2.45
  const mat: MaterialName = 'stuccoTan'
  const trim: MaterialName = 'stoneBlock'
  b.push(cx, base, zc, 0)

  const lp = ax - r - xL
  const rp = xR - (ax + r)
  // Piers run 0.35 m below the footprint base so no corner can lift off a
  // sloping alley floor.
  const pierH = ys + 0.45
  b.solid(mat, lp, pierH, depth, xL + lp / 2 - cx, pierH / 2 - 0.35, 0, 0, 0.05)
  b.solid(mat, rp, pierH, depth, xR - rp / 2 - cx, pierH / 2 - 0.35, 0, 0, 0.05)
  b.box(trim, lp + 0.12, 0.26, depth + 0.1, xL + lp / 2 - cx, ys + 0.12, 0, 0, 0.03)
  b.box(trim, rp + 0.12, 0.26, depth + 0.1, xR - rp / 2 - cx, ys + 0.12, 0, 0, 0.03)

  // Barrel vault through the full 3 m depth: the reveal is the point.
  const segs = 11
  const segLen = (Math.PI * r) / segs
  for (let i = 0; i < segs; i++) {
    const a = (Math.PI * (i + 0.5)) / segs
    b.geom(mat, chamferBox(segLen * 1.1, 0.42, depth, 0.02),
      xform(ax - cx + Math.cos(a) * (r + 0.21), ys + Math.sin(a) * (r + 0.21), 0, 0, 0, a - Math.PI / 2))
  }
  // Spandrel fill stepped to the extrados, then the solid mass over the gate.
  const R = r + 0.42
  const steps = 7
  for (let i = 0; i < steps; i++) {
    const y0 = ys + (R * i) / steps
    const y1 = ys + (R * (i + 1)) / steps
    const hw = Math.sqrt(Math.max(0, R * R - (y1 - ys) * (y1 - ys)))
    const l1 = ax - hw
    if (l1 - xL > 0.02) b.solid(mat, l1 - xL, y1 - y0, depth, (xL + l1) / 2 - cx, (y0 + y1) / 2, 0, 0, 0.02)
    const r0 = ax + hw
    if (xR - r0 > 0.02) b.solid(mat, xR - r0, y1 - y0, depth, (r0 + xR) / 2 - cx, (y0 + y1) / 2, 0, 0, 0.02)
  }
  const yTop = ys + R
  b.solid(mat, span, H - yTop, depth, 0, (yTop + H) / 2, 0, 0, 0.04)

  // Voussoirs on the face the camera sees, so the arch head catches a rim.
  const vous = 13
  for (let i = 0; i < vous; i++) {
    const a = Math.PI * ((i + 0.5) / vous)
    const dp = i % 2 === 0 ? 0.13 : 0.09
    b.geom(trim, chamferBox((Math.PI * r) / vous + 0.03, 0.36, dp, 0.014),
      xform(ax - cx + Math.cos(a) * (r + 0.12), ys + Math.sin(a) * (r + 0.12), -depth / 2 - dp / 2, 0, 0, a - Math.PI / 2))
  }
  b.box(trim, 0.34, 0.34, 0.12, ax - r - 0.17 - cx, ys, -depth / 2 - 0.05, 0, 0.02)
  b.box(trim, 0.34, 0.34, 0.12, ax + r + 0.17 - cx, ys, -depth / 2 - 0.05, 0, 0.02)

  // Shuttered window over the gate, recessed 0.3 m into the mass.
  const wy = yTop + 0.95
  b.box('plasterDamaged', 1.1, 1.3, 0.1, ax - cx + 0.35, wy, -depth / 2 + 0.3, 0, 0.01)
  b.box(trim, 1.4, 0.11, 0.5, ax - cx + 0.35, wy - 0.7, -depth / 2 + 0.1, 0, 0.03)
  b.box(trim, 1.5, 0.16, 0.42, ax - cx + 0.35, wy + 0.73, -depth / 2 + 0.08, 0, 0.03)
  for (let i = 0; i < 4; i++) {
    b.geom('rebar', cylinderGeom(0.017, 0.017, 1.24, 5),
      xform(ax - cx - 0.13 + i * 0.32, wy, -depth / 2 + 0.16))
  }

  // Coping, and a broken parapet so the roofline is not a ruled edge.
  b.box(trim, span + 0.24, 0.16, depth + 0.28, 0, H + 0.08, 0, 0, 0.035)
  let px = -span / 2 + 0.2
  while (px < span / 2 - 0.2) {
    const w = rng.range(0.5, 1.3)
    if (rng.bool(0.72)) {
      const ph = rng.range(0.35, 0.75)
      b.solid(mat, Math.min(w, span / 2 - 0.2 - px), ph, depth * 0.42, px + w / 2, H + 0.16 + ph / 2, -depth * 0.2, 0, 0.03)
    }
    px += w + rng.range(0.08, 0.5)
  }

  // A bare bulb on a flex under the vault: the only light in a 3 m tunnel, and
  // the thing that makes the far end read as a place rather than a hole.
  b.geom('metalPainted', cylinderGeom(0.006, 0.006, 0.6, 4), xform(ax - cx, ys + r - 0.3, 0.2))
  b.geom('glass', sphereGeom(0.06, 8, 6), xform(ax - cx, ys + r - 0.62, 0.2))
  b.geom('metalPainted', cylinderGeom(0.022, 0.028, 0.07, 8), xform(ax - cx, ys + r - 0.55, 0.2))

  // Damp and soot up the piers, and tyre rub where carts scrape the jambs.
  for (const sx of [-1, 1]) {
    b.geom('dirt', decalQuad(depth * 0.8, 2.0, 0.2, sx * 13),
      xform(ax - cx + sx * (r - 0.02), 1.05, 0.1, sx > 0 ? -Math.PI / 2 : Math.PI / 2))
  }
  b.geom('dirt', decalQuad(span * 0.9, 1.1, 0.18, 41), xform(0, 0.5, -depth / 2 - 0.014))
  b.geom('plasterDamaged', decalQuad(1.6, 1.9, 0.3, 7), xform(-span / 2 + 1.0, 3.4, -depth / 2 - 0.016))

  b.pop()
  result.indoor.push(new THREE.Box3().setFromCenterAndSize(
    new THREE.Vector3(ax, base + 1.2, zc), new THREE.Vector3(r * 1.8, 2.4, depth)))
}

/** Landmark on the skyline south-east: a steel water tower on braced legs. */
export function buildWaterTower(b: Builder, rng: Rand): void {
  const cx = 31
  const cz = 27.5
  const base = footprintBase(cx, cz, 5, 5)
  b.push(cx, base, cz, 0.3)
  const legH = 8.6
  const spread = 1.9
  for (let i = 0; i < 4; i++) {
    const a = (i * Math.PI) / 2 + Math.PI / 4
    const sx = Math.cos(a) * spread
    const sz = Math.sin(a) * spread
    const tx = Math.cos(a) * 1.15
    const tz = Math.sin(a) * 1.15
    const dx = tx - sx
    const dz = tz - sz
    const lean = Math.atan2(Math.hypot(dx, dz), legH)
    const m = xform((sx + tx) / 2, legH / 2, (sz + tz) / 2, Math.atan2(-dz, dx), 0, -lean)
    b.geom('metalRusted', chamferBox(0.16, legH + 0.4, 0.16, 0.02), m)
    b.collide(0.3, legH, 0.3, sx, legH / 2, sz, 0, 'metal')
  }
  for (const y of [2.6, 5.4]) {
    for (let i = 0; i < 4; i++) {
      const a0 = (i * Math.PI) / 2 + Math.PI / 4
      const a1 = ((i + 1) * Math.PI) / 2 + Math.PI / 4
      const t = 1 - y / legH
      const r0 = 1.15 + (spread - 1.15) * t
      const p0 = new THREE.Vector3(Math.cos(a0) * r0, y, Math.sin(a0) * r0)
      const p1 = new THREE.Vector3(Math.cos(a1) * r0, y, Math.sin(a1) * r0)
      const mid = p0.clone().add(p1).multiplyScalar(0.5)
      const len = p0.distanceTo(p1)
      b.geom('metalRusted', chamferBox(len, 0.09, 0.09, 0.015),
        xform(mid.x, mid.y, mid.z, Math.atan2(-(p1.z - p0.z), p1.x - p0.x)))
    }
  }
  b.geom('metalRusted', cylinderGeom(2.5, 2.2, 3.4, 16), xform(0, legH + 1.7, 0))
  b.geom('metalRusted', cylinderGeom(1.2, 2.5, 1.0, 16), xform(0, legH - 0.2, 0))
  b.geom('metalCorrugated', cylinderGeom(0.2, 2.6, 1.1, 16), xform(0, legH + 3.9, 0))
  b.collide(4.4, 3.4, 4.4, 0, legH + 1.7, 0, 0, 'metal')
  // Access ladder.
  for (let i = 0; i < 26; i++) {
    b.plate('metalRusted', 0.42, 0.035, 0.035, 0, 0.5 + i * 0.34, 2.62)
  }
  for (const sx of [-0.2, 0.2]) {
    b.geom('metalRusted', cylinderGeom(0.03, 0.03, 9.4, 6), xform(sx, 4.9, 2.62))
  }
  void rng
  b.pop()
}

// ---------------------------------------------------------------------------
// The city beyond the district
// ---------------------------------------------------------------------------

/**
 * Roof clutter for a backdrop block, in silhouette only.
 *
 * Every camera in the level sits well below these rooflines, so anything set
 * back behind its own parapet is invisible; the placement is deliberately
 * biased to the half of the roof facing the district. Sizes are chosen against
 * what the frame can actually resolve — at 110 m and a 80 degree vertical FOV
 * one metre is thirteen pixels, so a 2.4 m tank is a thirty-pixel silhouette
 * and a 7 m mast is a readable vertical, while anything under a metre is not
 * worth the triangles.
 */
function backdropRoofClutter(b: Builder, rng: Rand, w: number, d: number, top: number, scale = 1): void {
  const items = rng.int(1, 3)
  for (let i = 0; i < items; i++) {
    const px = rng.spread(Math.max(0.5, w / 2 - 1.6))
    const pz = -d / 2 + rng.range(0.9, Math.max(1.1, d * 0.5))
    switch (rng.int(0, 3)) {
      case 0: {
        // Water tank on a stand. The gap under the belly is what makes this
        // read as a tank rather than as another lump of the parapet.
        const r = rng.range(0.75, 1.2)
        const th = rng.range(1.7, 2.8)
        for (const sx of [-1, 1]) {
          for (const sz of [-1, 1]) {
            b.plate('metalRusted', 0.13, 0.95, 0.13, px + sx * r * 0.62, top + 0.48, pz + sz * r * 0.62)
          }
        }
        b.geom('metalRusted', cylinderGeom(r, r, th, 8), xform(px, top + 0.95 + th / 2, pz))
        break
      }
      case 1: {
        // Stair bulkhead — a second, smaller silhouette off one corner.
        const bw = rng.range(2.2, 3.6) * scale
        const bh = rng.range(2.1, 3.1) * scale
        b.plate('concreteWorn', bw, bh, rng.range(1.9, 3.0) * scale, px, top + bh / 2, pz)
        b.plate('metalRusted', bw + 0.3, 0.16, 0.3, px, top + bh + 0.08, pz - 0.5)
        break
      }
      case 2: {
        // Aerial mast with a crossarm and a guy anchor.
        const mh = rng.range(4.5, 9.5) * scale
        b.plate('metalRusted', 0.17, mh, 0.17, px, top + mh / 2, pz)
        b.plate('metalRusted', rng.range(1.3, 2.6) * scale, 0.12, 0.12, px, top + mh * rng.range(0.62, 0.86), pz)
        b.plate('metalRusted', rng.range(0.8, 1.6) * scale, 0.11, 0.11, px, top + mh * rng.range(0.4, 0.58), pz)
        break
      }
      default: {
        // Flue on a plinth.
        const ch = rng.range(2.2, 4.2) * scale
        b.plate('concreteWorn', 1.1, 0.5, 1.1, px, top + 0.25, pz)
        b.geom('metalRusted', cylinderGeom(0.32, 0.4, ch, 6), xform(px, top + 0.5 + ch / 2, pz))
        break
      }
    }
  }
}

/**
 * One block of the backdrop city, authored in a frame whose local -Z faces the
 * district. Everything is a plain box: a chamfer is a sub-pixel feature at
 * these ranges and costs nearly four times the geometry.
 *
 * `detail` is what the range can carry: 2 resolves individual windows, 1
 * resolves a floor rhythm but not a window, 0 is pure silhouette, and -1 is the
 * walled compound the outskirts are made of rather than a city block.
 */
function backdropBlock(
  b: Builder, rng: Rand,
  w: number, h: number, d: number,
  mat: MaterialName, detail: number,
): void {
  // Sunk well into the ground: out here the terrain is sampled on an 8 m grid,
  // and a block seated on the analytic height would show daylight under one
  // corner of itself.
  const buried = 5
  b.geom(mat, plainBox(w, h + buried, d), xform(0, (h - buried) / 2, 0))
  // Parapet. Every flat-roofed building in the district has one, and its
  // shadow line is the single feature that stops a distant block reading as an
  // extruded rectangle.
  b.plate('concreteWorn', w + 0.5, 0.72, d + 0.5, 0, h + 0.24, 0)

  // The edge of town is compounds, not city blocks. A wall standing clear of
  // the shed behind it puts two silhouettes and a strip of shadowed ground
  // between them onto what was a bare plane, which is the whole job out here.
  if (detail < 0) {
    const cw = w * rng.range(1.15, 1.5)
    const cd = d * rng.range(1.2, 1.7)
    const wallH = rng.range(2.0, 2.8)
    const t = 0.3
    for (const sz of [-1, 1]) {
      b.plate(mat, cw, wallH + 3, t, 0, (wallH - 3) / 2, (sz * cd) / 2)
    }
    for (const sx of [-1, 1]) {
      b.plate(mat, t, wallH + 3, cd, (sx * cw) / 2, (wallH - 3) / 2, 0)
    }
    // Gate piers, taller than the run, on the face that looks at the district.
    for (const sx of [-1, 1]) {
      b.plate('concreteWorn', 0.55, wallH + 3.7, 0.55, sx * rng.range(1.1, 1.8), (wallH + 0.7 - 3) / 2, -cd / 2)
    }
    backdropRoofClutter(b, rng, w, d, h + 0.6, 0.45)
    return
  }

  if (detail >= 2) {
    // Individual windows. At 60-140 m a 1.2 m opening covers eight to eighteen
    // pixels, which is exactly the scale that says "city" rather than "box".
    const bay = rng.range(2.7, 3.7)
    const storey = rng.range(3.0, 3.9)
    const cols = Math.max(1, Math.floor((w - 1.8) / bay))
    const rows = Math.max(1, Math.floor((h - 2.2) / storey))
    const ww = bay * rng.range(0.34, 0.46)
    const wh = storey * rng.range(0.4, 0.54)
    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        if (rng.bool(0.12)) continue // shuttered, bricked up, or in shadow
        const wx = -((cols - 1) * bay) / 2 + i * bay
        const wy = h - 1.0 - (j + 0.5) * storey
        if (wy < 1.4) continue
        b.plate(rng.bool(0.84) ? 'asphalt' : 'concreteWorn', ww, wh, 0.16, wx, wy, -d / 2 - 0.04)
      }
    }
    // String course under the lowest window: one continuous horizontal per
    // block, so the band of city has a shared datum line rather than a scatter
    // of unrelated rectangles.
    const course = h - 1.0 - (rows - 0.5) * storey - wh / 2 - 0.35
    if (course > 1.0) b.plate('concreteWorn', w + 0.18, 0.3, d + 0.18, 0, course, 0)
  } else if (detail === 1) {
    // Too far for a window, near enough for a floor rhythm. Broken into runs so
    // it reads as punched openings rather than ribbon glazing.
    const storey = rng.range(3.2, 4.4)
    const rows = Math.max(1, Math.floor((h - 2.6) / storey))
    for (let j = 0; j < rows; j++) {
      const wy = h - 1.3 - (j + 0.5) * storey
      if (wy < 1.6) continue
      const runs = rng.int(2, 4)
      for (let k = 0; k < runs; k++) {
        const rw = (w / runs) * rng.range(0.55, 0.86)
        const rx = -w / 2 + (w * (k + 0.5)) / runs
        b.plate('asphalt', rw, storey * rng.range(0.3, 0.42), 0.16, rx, wy, -d / 2 - 0.04)
      }
    }
  }

  // A setback upper storey. Two thirds of the horizon is more interesting than
  // a row of equal-topped boxes, and this is the cheapest way to buy a stepped
  // silhouette: one more box and one more coping.
  if (rng.bool(detail >= 2 ? 0.38 : 0.5)) {
    const sw = w * rng.range(0.4, 0.74)
    const sd = d * rng.range(0.45, 0.82)
    const sh = rng.range(2.6, 3.4) * rng.int(1, detail >= 2 ? 2 : 4)
    const ox = rng.spread((w - sw) / 2)
    const oz = rng.spread((d - sd) / 2)
    b.geom(mat, plainBox(sw, sh + 1.2, sd), xform(ox, h + 0.6 + (sh - 1.2) / 2, oz))
    b.plate('concreteWorn', sw + 0.4, 0.6, sd + 0.4, ox, h + 0.6 + sh + 0.2, oz)
    if (detail >= 1) backdropRoofClutter(b, rng, sw, sd, h + 0.6 + sh + 0.5)
  } else if (detail >= 1) {
    backdropRoofClutter(b, rng, w, d, h + 0.6)
  }
}

/**
 * A tall feature on the horizon. A skyline of nothing but blocks reads as a
 * texture; one vertical that is obviously a *thing* reads as a place, and the
 * eight graded poses look out along enough different bearings that these have
 * to be hand-placed rather than scattered.
 */
function backdropLandmark(b: Builder, rng: Rand, kind: number, h: number): void {
  switch (kind) {
    case 0: {
      // Minaret: shaft, gallery, upper shaft, cap.
      const r = h * 0.028
      b.geom('stuccoTan', cylinderGeom(r * 0.82, r * 1.15, h * 0.66, 10), xform(0, h * 0.33, 0))
      b.geom('concreteWorn', cylinderGeom(r * 1.5, r * 1.5, h * 0.045, 10), xform(0, h * 0.68, 0))
      b.geom('stuccoTan', cylinderGeom(r * 0.62, r * 0.78, h * 0.24, 10), xform(0, h * 0.82, 0))
      b.geom('concreteWorn', cylinderGeom(0.05, r * 0.95, h * 0.11, 10), xform(0, h * 0.98, 0))
      b.geom('concreteWorn', plainBox(h * 0.16, h * 0.05, h * 0.16), xform(0, 0.2, 0))
      break
    }
    case 1: {
      // Industrial flue with banding. Deliberately not one of the brick
      // materials: their texel is 0.9 m, which at 250 m is a quarter of a pixel
      // and shimmers rather than reads.
      const r = h * 0.035
      b.geom('plasterDamaged', cylinderGeom(r * 0.62, r, h, 10), xform(0, h / 2, 0))
      for (let i = 0; i < 4; i++) {
        const t = 0.45 + i * 0.16
        b.geom('metalRusted', cylinderGeom(r * (1.04 - t * 0.32), r * (1.06 - t * 0.32), h * 0.02, 10),
          xform(0, h * t, 0))
      }
      break
    }
    case 2: {
      // Lattice mast. Four battered legs and enough bracing to read as a truss.
      const foot = h * 0.075
      const bays = 8
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          for (let i = 0; i < bays; i++) {
            const t0 = i / bays
            const t1 = (i + 1) / bays
            const s0 = foot * (1 - t0 * 0.78)
            const s1 = foot * (1 - t1 * 0.78)
            const seg = h / bays
            b.geom('metalRusted', plainBox(0.16, Math.hypot(seg, s0 - s1), 0.16),
              xform(sx * (s0 + s1) / 2, h * (t0 + t1) / 2, sz * (s0 + s1) / 2))
          }
        }
      }
      for (let i = 1; i < bays; i++) {
        const s = foot * (1 - (i / bays) * 0.78) * 2
        for (const sz of [-1, 1]) {
          b.plate('metalRusted', s, 0.13, 0.13, 0, h * (i / bays), (sz * s) / 2)
          b.plate('metalRusted', 0.13, 0.13, s, (sz * s) / 2, h * (i / bays), 0)
        }
      }
      break
    }
    default: {
      // A slab tower with a stepped crown and a mast — the one thing on the
      // horizon that is taller than everything around it.
      const w = h * 0.3
      const d = h * 0.24
      b.geom('concreteWorn', plainBox(w, h + 6, d), xform(0, (h - 6) / 2, 0))
      b.plate('concreteWorn', w + 0.6, 0.8, d + 0.6, 0, h + 0.3, 0)
      const cw = w * 0.6
      b.geom('concreteWorn', plainBox(cw, h * 0.12 + 1.2, d * 0.62), xform(0, h + 0.6 + h * 0.06, 0))
      b.plate('concreteWorn', cw + 0.5, 0.6, d * 0.62 + 0.5, 0, h + 0.6 + h * 0.12 + 0.2, 0)
      b.plate('metalRusted', 0.2, h * 0.16, 0.2, 0, h + 0.7 + h * 0.2, 0)
      const storey = h * 0.075
      const rows = Math.floor((h - 4) / storey)
      for (let j = 0; j < rows; j++) {
        b.plate('asphalt', w * rng.range(0.68, 0.86), storey * 0.4, 0.18,
          rng.spread(w * 0.05), h - 2 - (j + 0.5) * storey, -d / 2 - 0.05)
      }
      break
    }
  }
}

/**
 * The city beyond the playable district.
 *
 * The previous version was three sparse rings of chamfered boxes in the same
 * bright palette as the near field, and the critique of the elevated pose was
 * exact: "the background is untextured white boxes standing on a flat tan plane
 * meeting the sky at a hard line". All three faults are structural.
 *
 * *The hard line.* The ground is modelled to {@link TERRAIN_EDGE} and then
 * stops. Nothing was reliably standing in front of that edge, so it read as the
 * boundary of the level. The near band here is **continuous in azimuth** and
 * placed entirely inside the terrain square, so from any camera in the level
 * the ground runs into a street of buildings before it can reach the sky.
 * `NEAR_ADVANCE` is under twice the half-angle the *narrowest* block subtends
 * at the *largest* radius, which makes the overlap a property of the
 * construction rather than of how the jitter happened to fall.
 *
 * *The boxes.* At this range only silhouette and value carry, so everything is
 * a plain box — but silhouette is exactly what a plain box does not have.
 * Parapets, setbacks, tanks, bulkheads and masts cost a few thousand triangles
 * across the whole horizon and are the difference between a skyline and a bar
 * chart.
 *
 * *The white.* The aerial pass floors transmittance at 0.45, so a distant
 * surface keeps nearly half its own albedo however far away it is; the brightest
 * material in the palette therefore stayed a bright slab at 200 m instead of
 * settling toward the haze. The palettes below are the mid and low end only,
 * and they get darker with distance.
 *
 * Nothing here casts, receives or collides.
 */
export function buildSkyline(b: Builder, rng: Rand): void {
  interface Band {
    rMin: number
    rMax: number
    wMin: number
    wMax: number
    dMin: number
    dMax: number
    hMin: number
    hMax: number
    /** Azimuth stepped between blocks, radians. */
    advance: number
    detail: number
    pal: MaterialName[]
    /** Chance a slot is left empty. Zero on the band that has to be continuous. */
    thin?: number
    /** Azimuth wedge to leave clear, in degrees. */
    clear?: [number, number]
  }

  // The near band's radius is capped so that even the deepest block keeps its
  // back wall inside the modelled ground: 134 + 26/2 = 147 < TERRAIN_EDGE.
  const NEAR_MAX = TERRAIN_EDGE - 18
  // atan(24 / (2 * 134)) = 0.0893 rad is the least any near block can subtend
  // from the middle of the level; two of those is 0.179, so an advance of 0.12
  // can never leave a gap however the radii and widths come out. Chosen as
  // large as that bound allows, because every metre of extra overlap here is
  // overdraw on the one band of the frame the whole horizon runs through.
  const NEAR_ADVANCE = 0.12
  const bands: Band[] = [
    // Outskirts: walled compounds and single-storey sheds on the open ground
    // between the district and the city. This is the other half of "a flat tan
    // plane" — the near band fixes where the plane meets the sky, this breaks
    // the plane itself. Left open across the bearing the highway runs out on,
    // so the one leading line the elevated pose is composed around still reads
    // as a road out of town rather than as a road into a wall.
    {
      rMin: 58, rMax: 96, wMin: 8, wMax: 17, dMin: 7, dMax: 14,
      hMin: 2.7, hMax: 5.6, advance: 0.2, detail: -1, thin: 0.42, clear: [36, 68],
      pal: ['stuccoTan', 'plasterDamaged', 'concreteWorn', 'stuccoTan', 'plasterOchre'],
    },
    {
      rMin: 100, rMax: NEAR_MAX, wMin: 24, wMax: 38, dMin: 14, dMax: 26,
      hMin: 9, hMax: 16.5, advance: NEAR_ADVANCE, detail: 2,
      pal: ['stuccoTan', 'plasterOchre', 'concreteWorn', 'plasterDamaged', 'stuccoTan'],
    },
    {
      rMin: 178, rMax: 246, wMin: 26, wMax: 50, dMin: 20, dMax: 40,
      hMin: 15, hMax: 33, advance: 0.15, detail: 1,
      pal: ['concreteWorn', 'stuccoTan', 'plasterDamaged', 'concreteWorn'],
    },
    {
      rMin: 292, rMax: 392, wMin: 34, wMax: 72, dMin: 26, dMax: 55,
      hMin: 24, hMax: 56, advance: 0.27, detail: 0,
      pal: ['concreteWorn', 'plasterDamaged', 'asphalt', 'concreteWorn'],
    },
  ]

  for (const band of bands) {
    // Walking azimuth rather than dividing a fixed count keeps the overlap
    // argument above true whatever the radii come out at.
    for (let a = rng.range(0, band.advance); a < Math.PI * 2; a += band.advance) {
      if (band.thin && rng.bool(band.thin)) continue
      if (band.clear) {
        const deg = (a * 180) / Math.PI
        if (deg > band.clear[0] && deg < band.clear[1]) continue
      }
      const r = rng.range(band.rMin, band.rMax)
      const x = Math.cos(a) * r
      const z = Math.sin(a) * r
      // Local -Z faces the origin, so the dressed face is the one in frame.
      b.push(x, groundHeight(x, z), z, Math.PI / 2 - a + rng.spread(0.06))
      backdropBlock(
        b, rng,
        rng.range(band.wMin, band.wMax),
        rng.range(band.hMin, band.hMax),
        rng.range(band.dMin, band.dMax),
        rng.pick(band.pal),
        band.detail,
      )
      b.pop()
    }
  }

  // Landmarks, on the bearings the graded poses actually look along: the
  // elevated shot runs out at 55 degrees, the low sun at 20, the two weapon
  // poses at 70, the alley at 92, the firefight at 110 and the plaza at 235.
  // Heights are held under 50 m: the sun sits at 21 degrees, and from the
  // middle of the level a 50 m mark at 250 m reaches 10 degrees, so nothing
  // here can eat the sun disc the low-sun pose is graded on.
  const marks: [number, number, number, number][] = [
    // azimuth (deg), radius, kind, height
    [56, 268, 0, 34],
    [22, 205, 1, 28],
    [74, 318, 3, 46],
    [96, 232, 2, 36],
    [113, 296, 0, 38],
    [162, 258, 1, 30],
    [238, 240, 0, 31],
    [292, 330, 2, 42],
  ]
  for (const [deg, radius, kind, height] of marks) {
    const a = (deg * Math.PI) / 180 + rng.spread(0.04)
    const r = radius * rng.range(0.94, 1.06)
    const x = Math.cos(a) * r
    const z = Math.sin(a) * r
    b.push(x, groundHeight(x, z) - 3, z, Math.PI / 2 - a)
    backdropLandmark(b, rng, kind, height * rng.range(0.92, 1.1))
    b.pop()
  }
}

/**
 * Everything a flat roof has that is not a machine: lapped felt, a stair
 * hatch, conduit, an outlet and the rubbish that collects up there.
 *
 * The elevated pose spends a third of its frame looking at one of these decks,
 * and it was a single unbroken slab. Lap seams cost almost nothing and give
 * that whole area a shadow rhythm and a sense of scale; the hatch and the
 * block stack give it two silhouettes to read against the parapet.
 */
function deckDressing(b: Builder, w: number, d: number, rng: Rand): void {
  // Bitumen sheet laid in strips, each lap standing proud of the last.
  const lap = 0.92
  const rolls = Math.max(2, Math.floor(d / lap))
  for (let i = 0; i < rolls; i++) {
    const z = -d / 2 + 0.35 + i * lap
    // Not every length is still down. Leaving a third of them torn back to
    // bare screed keeps the roof from becoming one uniform dark plane and
    // gives the surface a history of patching.
    if (rng.bool(0.7)) {
      b.plate('asphalt', w - 0.15, 0.016, lap - 0.03, 0, 0.008, z, rng.spread(0.012))
    } else {
      const cut = rng.range(0.32, 0.72)
      b.plate('asphalt', (w - 0.15) * cut, 0.016, lap - 0.03,
        -((w - 0.15) * (1 - cut)) / 2, 0.008, z, rng.spread(0.025))
    }
    b.slab('asphalt', w - 0.15, 0.026, 0.075, rng.spread(0.05), 0.017, z - lap / 2 + 0.04, rng.spread(0.01))
  }
  // Stair hatch: a kerb upstand with a boarded lid propped at one corner.
  const hx = rng.spread(w / 2 - 1.0)
  const hz = rng.spread(d / 2 - 1.0)
  b.push(hx, 0, hz, rng.range(0, Math.PI))
  for (const sx of [-1, 1]) b.slab('concreteWorn', 0.1, 0.26, 0.95, sx * 0.4, 0.13, 0)
  for (const sz of [-1, 1]) b.slab('concreteWorn', 0.7, 0.26, 0.1, 0, 0.13, sz * 0.42)
  b.geom('woodPlank', chamferBox(0.94, 0.05, 1.06, 0.012), xform(0.06, 0.31, 0, 0, 0, rng.range(0.04, 0.16)))
  b.collide(1.0, 0.35, 1.1, 0, 0.18, 0, 0, 'wood')
  b.pop()
  // Conduit run clipped to the deck, with saddles.
  const cz = rng.spread(d / 2 - 0.8)
  b.geom('metalPainted', cylinderGeom(0.026, 0.026, w - 0.5, 6), xform(0, 0.055, cz, 0, 0, Math.PI / 2))
  for (let i = 0; i < Math.floor(w / 1.2); i++) {
    b.slab('metalRusted', 0.07, 0.055, 0.09, -w / 2 + 0.5 + i * 1.2, 0.028, cz)
  }
  // Rainwater outlet, sunk into a shallow dished area.
  const ox = rng.spread(w / 2 - 0.6)
  const oz = d / 2 - 0.45
  b.geom('metalRusted', cylinderGeom(0.12, 0.14, 0.05, 10), xform(ox, 0.02, oz))
  b.geom('dirt', decalQuad(1.1, 0.9, 0.3, ox * 5 + oz), xform(ox, 0.005, oz, 0, -Math.PI / 2))
  // A stack of spare blocks and a couple of tiles, left after a repair. Every
  // course is offset and turned: a plumb, square stack is a loop, not a person.
  const bx = rng.spread(w / 2 - 0.7)
  const bz = rng.spread(d / 2 - 0.7)
  b.push(bx, 0, bz, rng.range(0, Math.PI))
  for (let i = 0; i < rng.int(3, 7); i++) {
    b.box('concreteRubble', 0.44, 0.2, 0.21,
      rng.spread(0.07), 0.1 + i * 0.203, rng.spread(0.07), rng.spread(0.38), 0.015)
  }
  b.pop()

  // Bay joints saw-cut across the screed. The lap strips already give the deck
  // a rhythm one way; without the cross-joints the elevated pose still reads a
  // metre-wide corduroy rather than a slab that was poured in panels.
  for (let i = 1; i * 1.35 < w; i++) {
    const jx = -w / 2 + i * 1.35 + rng.spread(0.06)
    b.plate('asphalt', 0.028, 0.02, d - 0.2, jx, 0.006, rng.spread(0.05))
  }

  // Cracked and patched screed. A roof deck is poured in one go and then
  // repaired for thirty years; the patch edges are the only thing standing
  // between this surface and a flat grey plane, and they cost two triangles
  // each. Three of them plus a crack line breaks the whole field.
  for (let i = 0; i < 4; i++) {
    const px = rng.spread(w / 2 - 0.5)
    const pz = rng.spread(d / 2 - 0.5)
    b.slab(rng.bool(0.5) ? 'concreteWorn' : 'asphalt',
      rng.range(0.6, 1.7), 0.022, rng.range(0.5, 1.4), px, 0.014, pz, rng.range(0, Math.PI))
  }
  {
    let cx = -w / 2 + rng.range(0.2, 0.8)
    let cz = rng.spread(d / 2 - 0.6)
    while (cx < w / 2 - 0.2) {
      const seg = rng.range(0.4, 1.1)
      const dz = rng.spread(0.5)
      b.plate('asphalt', Math.hypot(seg, dz), 0.012, 0.035, cx + seg / 2, 0.008, cz + dz / 2,
        Math.atan2(-dz, seg))
      cx += seg
      cz += dz
    }
  }

  // Vent stack with a cowl: the one thing on a flat roof that breaks the
  // parapet line from a low camera.
  {
    const vx = rng.spread(w / 2 - 0.6)
    const vz = rng.spread(d / 2 - 0.6)
    const vh = rng.range(0.7, 1.3)
    b.geom('concreteWorn', chamferBox(0.36, 0.22, 0.36, 0.02), xform(vx, 0.11, vz))
    b.geom('metalRusted', cylinderGeom(0.09, 0.1, vh, 8), xform(vx, 0.22 + vh / 2, vz))
    b.geom('metalRusted', cylinderGeom(0.16, 0.05, 0.16, 10), xform(vx, 0.22 + vh + 0.06, vz))
    b.geom('dirt', decalQuad(0.7, 0.7, 0.3, vx * 5 + vz), xform(vx, 0.007, vz, 0, -Math.PI / 2))
  }

  // Wind-blown dust banked against the parapet on the lee side.
  for (const sz of [-1, 1]) {
    b.geom('sand', rampPrism(w * 0.8, rng.range(0.06, 0.13), 0.5),
      xform(rng.spread(0.4), 0.0, sz * (d / 2 - 0.25), sz > 0 ? 0 : Math.PI))
  }
  for (const sx of [-1, 1]) {
    b.geom('sand', rampPrism(d * 0.7, rng.range(0.05, 0.11), 0.42),
      xform(sx * (w / 2 - 0.22), 0.0, rng.spread(0.4), sx > 0 ? -Math.PI / 2 : Math.PI / 2))
  }
}

/**
 * A split-system condenser, modelled as the machine it is.
 *
 * The version this replaces was a 0.86 x 0.62 x 0.36 box with the fan disc
 * buried entirely inside it and a rust-coloured plate under the base, and in
 * the elevated pose it filled the foreground as three featureless slabs. The
 * parts that make it read are all silhouette: the guard ring standing proud of
 * the discharge face, the coil fins down the back, the lid lip, and above all
 * the 9 cm gap under it — feet let daylight and a contact shadow under the
 * body, which is what stops a prop looking stamped onto the floor.
 */
function condenser(b: Builder, x: number, z: number, yaw: number, rng: Rand): void {
  const w = 0.86 * rng.range(0.92, 1.1)
  const h = 0.6 * rng.range(0.9, 1.15)
  const d = 0.36
  const foot = 0.09
  b.push(x, 0, z, yaw)
  // Angle-iron stand: two rails on four short feet.
  for (const sz of [-1, 1]) {
    b.slab('metalRusted', w + 0.08, 0.035, 0.07, 0, foot - 0.018, sz * (d / 2 - 0.05))
    for (const sx of [-1, 1]) {
      b.slab('metalRusted', 0.07, foot, 0.07, sx * (w / 2 - 0.07), foot / 2, sz * (d / 2 - 0.05))
    }
  }
  const cy = foot + h / 2
  b.box('metalPainted', w, h, d, 0, cy, 0, 0, 0.03)
  b.collide(w, h + foot, d, 0, (h + foot) / 2, 0, 0, 'thinMetal')
  // Pressed lid with a lip, so the top is not one flat plane.
  b.slab('metalPainted', w - 0.05, 0.03, d - 0.05, 0, foot + h + 0.012, 0)
  b.slab('metalPainted', w + 0.03, 0.022, d + 0.03, 0, foot + h - 0.03, 0)
  // Discharge guard: a raised ring with radial bars over a recessed fan.
  const r = Math.min(0.25, h * 0.42)
  b.geom('steelBrushed', cylinderGeom(r, r, 0.05, 14), xform(0, cy, -d / 2 + 0.02, 0, Math.PI / 2))
  b.geom('metalRusted', cylinderGeom(r + 0.035, r + 0.035, 0.03, 16, false), xform(0, cy, -d / 2 - 0.035, 0, Math.PI / 2))
  for (let k = 0; k < 5; k++) {
    b.geom('metalRusted', plainBox(r * 2, 0.014, 0.014),
      xform(0, cy, -d / 2 - 0.045, 0, 0, (k * Math.PI) / 5))
  }
  // Coil fins down the back face.
  const fins = 9
  for (let k = 0; k < fins; k++) {
    b.geom('steelBrushed', plainBox(0.02, h - 0.09, 0.028),
      xform(-w / 2 + 0.06 + (k * (w - 0.12)) / (fins - 1), cy, d / 2 - 0.005))
  }
  // Refrigerant pair leaving the side, lagged, elbowing down to the deck.
  const px = w / 2 + 0.03
  for (const off of [-0.06, 0.06]) {
    b.geom('plasterWhite', cylinderGeom(0.028, 0.028, cy - 0.1, 6), xform(px, (cy - 0.1) / 2 + 0.05, off))
    b.geom('plasterWhite', cylinderGeom(0.028, 0.028, 0.24, 6), xform(px - 0.12, cy, off, 0, 0, Math.PI / 2))
  }
  b.slab('metalPainted', 0.13, 0.17, 0.06, px + 0.02, cy + 0.16, 0)
  // Condensate stain running out from under the stand.
  b.geom('dirt', decalQuad(w + 0.5, d + 0.7, 0.28, x * 3 + z), xform(0.1, 0.006, 0.1, 0, -Math.PI / 2))
  b.pop()
}

/** Roof clutter shared by every deck: AC units, dishes, tanks, aerials. */
export function buildRoofClutter(b: Builder, decks: BuildResult['decks'], rng: Rand): void {
  for (const deck of decks) {
    if (deck.w < 2 || deck.d < 2) continue
    b.push(deck.cx, deck.y, deck.cz, deck.yaw)
    deckDressing(b, deck.w, deck.d, rng)
    const n = Math.max(3, Math.round((deck.w * deck.d) / 11))
    for (let i = 0; i < n; i++) {
      const x = rng.spread(deck.w / 2 - 0.5)
      const z = rng.spread(deck.d / 2 - 0.5)
      const kind = rng.next()
      if (kind < 0.28) {
        // Water tank on a stand: hoop bands, a lid, a float valve and the pipe
        // run down to the deck. A plain cylinder on four sticks was reading as
        // a placeholder in the elevated pose, where it is the nearest silhouette
        // in frame.
        const r = rng.range(0.45, 0.65)
        const th = rng.range(0.85, 1.25)
        const stand = 0.5
        for (let l = 0; l < 4; l++) {
          const a = (l * Math.PI) / 2 + 0.7
          const lx = x + Math.cos(a) * r * 0.7
          const lz = z + Math.sin(a) * r * 0.7
          b.slab('metalRusted', 0.07, stand, 0.07, lx, stand / 2, lz)
          b.slab('concreteRubble', 0.2, 0.06, 0.2, lx, 0.03, lz)
        }
        for (let l = 0; l < 4; l++) {
          const a0 = (l * Math.PI) / 2 + 0.7
          const a1 = ((l + 1) * Math.PI) / 2 + 0.7
          const p0x = Math.cos(a0) * r * 0.7
          const p0z = Math.sin(a0) * r * 0.7
          const p1x = Math.cos(a1) * r * 0.7
          const p1z = Math.sin(a1) * r * 0.7
          b.slab('metalRusted', Math.hypot(p1x - p0x, p1z - p0z), 0.05, 0.05,
            x + (p0x + p1x) / 2, stand - 0.06, z + (p0z + p1z) / 2, Math.atan2(-(p1z - p0z), p1x - p0x))
        }
        b.geom('metalPainted', cylinderGeom(r, r, th, 14), xform(x, stand + th / 2, z))
        for (const t of [0.25, 0.72]) {
          b.geom('metalRusted', cylinderGeom(r + 0.02, r + 0.02, 0.05, 14, false), xform(x, stand + th * t, z))
        }
        b.geom('metalRusted', cylinderGeom(r * 0.34, r * 0.34, 0.06, 10), xform(x, stand + th + 0.03, z))
        b.geom('metalPainted', cylinderGeom(0.03, 0.03, stand + th * 0.6, 6),
          xform(x + r * 0.92, (stand + th * 0.6) / 2, z))
        b.geom('metalPainted', cylinderGeom(0.03, 0.03, r * 0.9, 6),
          xform(x + r * 0.5, stand + th * 0.6, z, 0, 0, Math.PI / 2))
        b.geom('metalRusted', decalQuad(r * 1.1, th * 0.7, 0.3, x * 3 + z), xform(x, stand + th * 0.4, z - r - 0.01))
        b.collide(r * 2, stand + th, r * 2, x, (stand + th) / 2, z, 0, 'thinMetal')
      } else if (kind < 0.52) {
        condenser(b, x, z, rng.range(0, Math.PI * 2), rng)
      } else if (kind < 0.72) {
        // Satellite dish, angled at the sky.
        const yaw = rng.range(0, Math.PI * 2)
        const r = rng.range(0.34, 0.55)
        b.geom('metalPainted', cylinderGeom(0.05, 0.06, 0.9, 8), xform(x, 0.45, z))
        b.geom('plasterWhite', sphereGeom(r, 14, 8, Math.PI * 2, 0.62),
          xform(x, 0.95, z, yaw, Math.PI * 0.62))
        b.plate('metalRusted', 0.42, 0.05, 0.42, x, 0.02, z, yaw)
      } else if (kind < 0.86) {
        // A boarded crate: corner battens and real boards, so the roofline gets
        // a broken silhouette instead of another plain cube.
        const h = rng.range(0.45, 0.9)
        b.push(x, 0, z, rng.range(0, 3.1))
        b.box('woodCrate', 0.758, h - 0.006, 0.658, 0, h / 2, 0, 0, 0.014)
        for (const sx of [-1, 1]) {
          for (const sz of [-1, 1]) b.plate('woodCrate', 0.07, h, 0.07, sx * 0.365, h / 2, sz * 0.315)
        }
        const courses = Math.max(3, Math.round(h / 0.15))
        const bh = (h - 0.009 * (courses - 1)) / courses
        for (let k = 0; k < courses; k++) {
          const yy = bh / 2 + k * (bh + 0.009)
          for (const sz of [-1, 1]) b.plate('woodCrate', 0.66, bh, 0.021, 0, yy, sz * 0.3395)
          for (const sx of [-1, 1]) b.plate('woodCrate', 0.021, bh, 0.56, sx * 0.3895, yy, 0)
        }
        b.collide(0.8, h, 0.7, 0, h / 2, 0, 0, 'wood')
        b.pop()
      } else {
        // Aerial mast: a proper Yagi with a boom, a reflector and a graded
        // element run, plus real guys down to the deck. This is the highest
        // frequency silhouette on any roofline and it sells the skyline.
        const h = rng.range(1.8, 3.4)
        const yaw = rng.range(0, Math.PI * 2)
        b.geom('metalRusted', cylinderGeom(0.03, 0.045, h, 6), xform(x, h / 2, z))
        b.slab('concreteRubble', 0.36, 0.1, 0.36, x, 0.05, z)
        const boom = rng.range(0.9, 1.5)
        b.geom('metalRusted', cylinderGeom(0.014, 0.014, boom, 4), xform(x, h - 0.1, z, yaw, 0, Math.PI / 2))
        for (let k = 0; k < 7; k++) {
          const t = k / 6
          const ex = x + Math.cos(yaw) * (boom * (t - 0.5))
          const ez = z - Math.sin(yaw) * (boom * (t - 0.5))
          b.plate('metalRusted', 0.62 - t * 0.3, 0.014, 0.014, ex, h - 0.1, ez, yaw + Math.PI / 2)
        }
        for (let k = 0; k < 3; k++) {
          const a = (k / 3) * Math.PI * 2 + 0.5
          b.geom('metalRusted', catenary(
            new THREE.Vector3(x, h - 0.25, z),
            new THREE.Vector3(x + Math.cos(a) * 1.3, 0.05, z + Math.sin(a) * 1.3),
            0.06, 0.008, 4, 3))
        }
      }
    }
    // Laundry line across the deck, with something actually pegged to it.
    if (rng.bool(0.75) && deck.w > 3.5) {
      const px = deck.w / 2 - 0.4
      b.geom('metalRusted', cylinderGeom(0.04, 0.04, 1.5, 6), xform(-px, 0.75, 0))
      b.geom('metalRusted', cylinderGeom(0.04, 0.04, 1.5, 6), xform(px, 0.75, 0))
      b.slab('metalRusted', 0.5, 0.05, 0.05, -px, 1.46, 0)
      b.slab('metalRusted', 0.5, 0.05, 0.05, px, 1.46, 0)
      for (const off of [-0.16, 0.16]) {
        const p0 = new THREE.Vector3(-px, 1.44, off)
        const p1 = new THREE.Vector3(px, 1.44, off)
        b.geom('metalRusted', catenary(p0, p1, 0.13, 0.008, 8, 4))
        let t = rng.range(0.08, 0.24)
        while (t < 0.84) {
          const t1 = Math.min(0.92, t + rng.range(0.09, 0.2))
          const a = p0.clone().lerp(p1, t)
          const c = p0.clone().lerp(p1, t1)
          a.y -= Math.sin(Math.PI * t) * 0.13
          c.y -= Math.sin(Math.PI * t1) * 0.13
          const drop = rng.range(0.4, 0.95)
          b.geom(rng.pick<MaterialName>(['fabricAwning', 'tarp', 'sandbag']), clothQuad(
            a, c,
            new THREE.Vector3(c.x, c.y - drop, c.z),
            new THREE.Vector3(a.x, a.y - drop - rng.spread(0.12), a.z),
            rng.range(0.02, 0.07), rng.range(0.02, 0.06), 4, 4, t * 37))
          t = t1 + rng.range(0.04, 0.14)
        }
      }
    }
    b.pop()
  }
}

export { bayPositions, sidePlacement, xform }
